import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { notificar } from '../utils/notify';
import '../styles/Gestao_contabil.css';

// ── Peças de gráfico da aba de auditoria ─────────────────────────────────
// Tooltip escuro e compacto: lê melhor sobre áreas coloridas que o padrão branco.
const TooltipAud = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#0f172a', color: '#fff', borderRadius: 10, padding: '10px 12px',
                  boxShadow: '0 10px 30px -10px rgba(15,23,42,.5)', fontSize: 12, minWidth: 150 }}>
      {label != null && <div style={{ opacity: .7, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', fontSize: 11 }}>{label}</div>}
      {payload.map(p => (
        <div key={p.dataKey || p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 99, background: p.color || p.payload?.color, marginRight: 6 }}></span>{p.name}</span>
          <b style={{ fontVariantNumeric: 'tabular-nums' }}>{p.value}</b>
        </div>
      ))}
    </div>
  );
};

// Donut com o total no centro e legenda lateral com valor e %.
const DonutAud = ({ dados, rotuloCentro }) => {
  const total = dados.reduce((x, d) => x + d.value, 0);
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap flex-md-nowrap">
      <div style={{ position: 'relative', width: 220, height: 220, flex: '0 0 220px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={dados} dataKey="value" nameKey="name" cx="50%" cy="50%"
              innerRadius={68} outerRadius={100} paddingAngle={2} cornerRadius={6} stroke="#fff" strokeWidth={2}>
              {dados.map((e, i) => <Cell key={i} fill={e.color} />)}
            </Pie>
            <Tooltip content={<TooltipAud />} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{total}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 4 }}>{rotuloCentro}</div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1" style={{ minWidth: 160 }}>
        {dados.map(d => (
          <div key={d.name} className="mb-2">
            <div className="d-flex justify-content-between align-items-center" style={{ fontSize: 13 }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: d.color, marginRight: 8 }}></span>{d.name}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}><b>{d.value}</b> <span className="text-muted">{total ? Math.round((d.value / total) * 100) : 0}%</span></span>
            </div>
            <div style={{ height: 4, background: '#eef1f5', borderRadius: 99, marginTop: 4 }}>
              <div style={{ height: 4, borderRadius: 99, background: d.color, width: `${total ? (d.value / total) * 100 : 0}%` }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EIXO_AUD = { tickLine: false, axisLine: false, tick: { fill: '#64748b', fontSize: 11 } };

const Gestao_contabil = () => {
  // Estados principais
  const [historicoCompleto, setHistoricoCompleto] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [abaAtiva, setAbaAtiva] = useState('status-atual');
  const [statusAtual, setStatusAtual] = useState({});
  const [carregandoStatus, setCarregandoStatus] = useState(false);
  const [historicoAtrasos, setHistoricoAtrasos] = useState([]);
  const [carregandoAtrasos, setCarregandoAtrasos] = useState(false);
  const [filtroAtrasos, setFiltroAtrasos] = useState('');
  const [filtroOperadorAtrasos, setFiltroOperadorAtrasos] = useState('');
  const [estatisticasOperadoresAtrasos, setEstatisticasOperadoresAtrasos] = useState({});
  const [empresasInadimplentes, setEmpresasInadimplentes] = useState([]);
  const [carregandoInadimplencia, setCarregandoInadimplencia] = useState(false);
  const [filtroInadimplencia, setFiltroInadimplencia] = useState('');
  const [filtroObservacaoInadimplencia, setFiltroObservacaoInadimplencia] = useState('');
  const [clientesPorOperador, setClientesPorOperador] = useState([]);
  const [carregandoClientesOperador, setCarregandoClientesOperador] = useState(false);
  const [carregandoAcesso, setCarregandoAcesso] = useState(true);
  const [acessoNegado, setAcessoNegado] = useState(false);
  const [dadosUsuario, setDadosUsuario] = useState(null);
  const [filtroOperador, setFiltroOperador] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState('30');
  const [filtroTipoEntrega, setFiltroTipoEntrega] = useState('');
  const [operadoresUnicos, setOperadoresUnicos] = useState([]);
  const [estatisticasGerais, setEstatisticasGerais] = useState({});
  const [estatisticasPorOperador, setEstatisticasPorOperador] = useState([]);
  const [evolucaoMensal, setEvolucaoMensal] = useState([]);
  const [pendenciasParciais, setPendenciasParciais] = useState([]);
  const [pendenciasResolvidasPorOperador, setPendenciasResolvidasPorOperador] = useState([]);
  const [insightsIA, setInsightsIA] = useState({});
  const [rankingOperadores, setRankingOperadores] = useState([]);
  const [evolucaoSemanal, setEvolucaoSemanal] = useState([]);
  const [analiseHorarios, setAnaliseHorarios] = useState([]);
  const [tendencias, setTendencias] = useState({});
  const [modalObservacao, setModalObservacao] = useState({ show: false, texto: '', empresa: '' });
  const [modalEntregas, setModalEntregas] = useState({ show: false });
  const [entregasFiltradas, setEntregasFiltradas] = useState([]);

  // Envio de Fechamento
  const [historicoFechamentos, setHistoricoFechamentos] = useState([]);
  const [carregandoFechamentos, setCarregandoFechamentos] = useState(false);

  // Gestão de Auditorias
  const [auditorias, setAuditorias] = useState([]);
  const [carregandoAuditorias, setCarregandoAuditorias] = useState(false);
  // carteira ativa (para cobertura da auditoria por tributação)
  const [carteiraAud, setCarteiraAud] = useState([]);
  const [modalCobertura, setModalCobertura] = useState(null);      // chave do regime aberto
  const [evolAgrupar, setEvolAgrupar] = useState('mes');          // 'mes' | 'semana'
  const [evolPeriodo, setEvolPeriodo] = useState('180');          // dias ou 'todos'
  const [rankRegime, setRankRegime] = useState('');               // '' | 'SN' | 'LP' | 'LR' | 'OUTROS'
  const [rankOrdem, setRankOrdem] = useState('mais');             // 'mais' | 'menos' erros
  const [audDe, setAudDe] = useState('');                          // AAAA-MM-DD, data da auditoria
  const [audAte, setAudAte] = useState('');
  const [verTodosPend, setVerTodosPend] = useState(false);

  // Cores para gráficos
  const CORES = {
    completa: '#28a745',
    parcial: '#ffc107',
    atrasada: '#dc3545',
    atrasadaParcial: '#fd7e14',
    emDia: '#17a2b8',
    resolvido: '#6f42c1'
  };

  const CORES_OPERADORES = [
    '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
    '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b'
  ];

  // Função para verificar acesso
  const verificarAcesso = useCallback(async () => {
    setCarregandoAcesso(true);
    const accessToken = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      if (response.data.success) {
        const { usuario, first_name, grupo, name } = response.data;
        
        setDadosUsuario({
          username: usuario,
          first_name: first_name,
          grupo: grupo,
          name: name
        });

        if (grupo !== 'adm') {
          setAcessoNegado(true);
        } else {
          setAcessoNegado(false);
        }
      } else {
        setAcessoNegado(true);
      }
    } catch (error) {
      console.error('Erro ao verificar acesso:', error);
      setAcessoNegado(true);
    } finally {
      setCarregandoAcesso(false);
    }
  }, []);

  useEffect(() => {
    verificarAcesso();
  }, [verificarAcesso]);

  // Carregar histórico de atrasos
  const carregarHistoricoAtrasos = useCallback(async () => {
    setCarregandoAtrasos(true);
    const accessToken = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/historico-atrasos/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      if (response.data.success) {
        setHistoricoAtrasos(response.data.historico_atrasos || []);
        setEstatisticasOperadoresAtrasos(response.data.estatisticas_operadores || {});
      }
      
      setCarregandoAtrasos(false);
    } catch (error) {
      console.error('Erro ao carregar histórico de atrasos:', error);
      setMensagem('Erro ao carregar histórico de atrasos');
      setCarregandoAtrasos(false);
    }
  }, []);

  useEffect(() => {
    if (abaAtiva === 'historico-atrasos' && !acessoNegado && !carregandoAcesso) {
      carregarHistoricoAtrasos();
    }
  }, [abaAtiva, carregarHistoricoAtrasos, acessoNegado, carregandoAcesso]);

  const carregarHistoricoFechamentos = useCallback(async () => {
    setCarregandoFechamentos(true);
    const accessToken = localStorage.getItem('access');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/historico-relatorios/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.data.sucesso) {
        setHistoricoFechamentos(response.data.historico || []);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico de fechamentos:', error);
    } finally {
      setCarregandoFechamentos(false);
    }
  }, []);

  useEffect(() => {
    if (abaAtiva === 'envio-fechamento' && !acessoNegado && !carregandoAcesso) {
      carregarHistoricoFechamentos();
    }
  }, [abaAtiva, carregarHistoricoFechamentos, acessoNegado, carregandoAcesso]);

  const carregarAuditorias = useCallback(async () => {
    setCarregandoAuditorias(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/listar-historico-auditoria/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuditorias(res.data.auditorias || []);
      // carteira ativa: base da cobertura da auditoria por tributação
      try {
        const rc = await axios.get(`${process.env.REACT_APP_API_BASE}/api/empresas-contabil/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCarteiraAud(rc.data.empresas || []);
      } catch { setCarteiraAud([]); }
    } catch {}
    finally { setCarregandoAuditorias(false); }
  }, []);

  useEffect(() => {
    if (abaAtiva === 'auditoria' && !acessoNegado && !carregandoAcesso) {
      carregarAuditorias();
    }
  }, [abaAtiva, carregarAuditorias, acessoNegado, carregandoAcesso]);

  // Carregar histórico
  const carregarHistorico = useCallback(async () => {
    setCarregando(true);
    const accessToken = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/historico-contabil/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      const historico = response.data.historico || [];
      setHistoricoCompleto(historico);

      const operadores = [...new Set(historico.map(h => h.operador).filter(Boolean))].sort();
      setOperadoresUnicos(operadores);

      setCarregando(false);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setMensagem('Erro ao carregar dados do histórico');
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!acessoNegado && !carregandoAcesso) {
      carregarHistorico();
    }
  }, [carregarHistorico, acessoNegado, carregandoAcesso]);

  // Carregar empresas inadimplentes
  const carregarInadimplentes = useCallback(async () => {
    setCarregandoInadimplencia(true);
    const accessToken = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/listar-empresas-atraso-10-dias/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      if (response.data.success) {
        setEmpresasInadimplentes(response.data.empresas || []);
      }
      
      setCarregandoInadimplencia(false);
    } catch (error) {
      console.error('Erro ao carregar inadimplentes:', error);
      setMensagem('Erro ao carregar dados de inadimplência');
      setCarregandoInadimplencia(false);
    }
  }, []);

  useEffect(() => {
    if ((abaAtiva === 'inadimplencia' || abaAtiva === 'status-atual') && !acessoNegado && !carregandoAcesso) {
      carregarInadimplentes();
    }
  }, [abaAtiva, carregarInadimplentes, acessoNegado, carregandoAcesso]);

  // Carregar clientes por operador
  const carregarClientesPorOperador = useCallback(async () => {
    setCarregandoClientesOperador(true);
    const accessToken = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/listar-empresas-atraso-10-dias/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      if (response.data.success) {
        const empresas = response.data.empresas || [];
        
        const contagemPorOperador = {};
        empresas.forEach(emp => {
          const operador = emp.operador || 'Não identificado';
          if (!contagemPorOperador[operador]) {
            contagemPorOperador[operador] = {
              operador: operador,
              total: 0,
              comAtraso: 0,
              semObservacao: 0
            };
          }
          contagemPorOperador[operador].total++;
          contagemPorOperador[operador].comAtraso++;
          
          if (!emp.observacao || emp.observacao.trim() === '') {
            contagemPorOperador[operador].semObservacao++;
          }
        });

        const arrayOperadores = Object.values(contagemPorOperador)
          .sort((a, b) => b.total - a.total);

        setClientesPorOperador(arrayOperadores);
      }
      
      setCarregandoClientesOperador(false);
    } catch (error) {
      console.error('Erro ao carregar clientes por operador:', error);
      setCarregandoClientesOperador(false);
    }
  }, []);

  useEffect(() => {
    if ((abaAtiva === 'operadores' || abaAtiva === 'status-atual') && !acessoNegado && !carregandoAcesso) {
      carregarClientesPorOperador();
    }
  }, [abaAtiva, carregarClientesPorOperador, acessoNegado, carregandoAcesso]);

  // Carregar status atual
  const carregarStatusAtual = useCallback(async () => {
    setCarregandoStatus(true);
    const accessToken = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/empresas-contabil/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );

      const empresas = response.data.empresas || [];
      
      const emDia = empresas.filter(emp => 
        emp.Status_contabil && emp.Status_contabil.toLowerCase().includes('em dia')
      ).length;
      
      const atrasadas = empresas.filter(emp => 
        emp.Status_contabil && emp.Status_contabil.toLowerCase().includes('atrasado')
      ).length;
      
      const porOperador = {};
      empresas.forEach(emp => {
        const op = emp.operador || 'Não identificado';
        if (!porOperador[op]) {
          porOperador[op] = { total: 0, emDia: 0, atrasadas: 0 };
        }
        porOperador[op].total++;
        
        if (emp.Status_contabil) {
          if (emp.Status_contabil.toLowerCase().includes('em dia')) {
            porOperador[op].emDia++;
          } else if (emp.Status_contabil.toLowerCase().includes('atrasado')) {
            porOperador[op].atrasadas++;
          }
        }
      });
      
      const rankingAtrasos = Object.entries(porOperador)
        .map(([operador, stats]) => ({
          operador,
          ...stats,
          percentualAtraso: stats.total > 0 ? ((stats.atrasadas / stats.total) * 100).toFixed(1) : 0
        }))
        .sort((a, b) => b.atrasadas - a.atrasadas);
      
      setStatusAtual({
        total: empresas.length,
        emDia,
        atrasadas,
        percentualEmDia: empresas.length > 0 ? ((emDia / empresas.length) * 100).toFixed(1) : 0,
        percentualAtrasadas: empresas.length > 0 ? ((atrasadas / empresas.length) * 100).toFixed(1) : 0,
        porOperador: rankingAtrasos,
        empresasDetalhadas: empresas
      });
      
      setCarregandoStatus(false);
    } catch (error) {
      console.error('Erro ao carregar status atual:', error);
      setCarregandoStatus(false);
    }
  }, []);

  useEffect(() => {
    if ((abaAtiva === 'status-atual' || abaAtiva === 'visaoGeral') && !acessoNegado && !carregandoAcesso) {
      carregarStatusAtual();
    }
  }, [abaAtiva, carregarStatusAtual, acessoNegado, carregandoAcesso]);

  // Filtrar dados por período
  const filtrarPorPeriodo = useCallback((dados) => {
    if (!filtroPeriodo || filtroPeriodo === 'todos') return dados;

    const diasFiltro = parseInt(filtroPeriodo);
    const dataLimite = new Date();
    dataLimite.setDate(dataLimite.getDate() - diasFiltro);

    return dados.filter(item => {
      if (!item.data_hoje) return false;
      
      const partes = item.data_hoje.split(' ')[0].split('/');
      if (partes.length !== 3) return false;
      
      const dataItem = new Date(
        parseInt(partes[2]),
        parseInt(partes[1]) - 1,
        parseInt(partes[0])
      );
      
      return dataItem >= dataLimite;
    });
}, [filtroPeriodo]);

  // Processar estatísticas quando os dados mudam
  useEffect(() => {
    if (historicoCompleto.length === 0) return;

    let dadosFiltrados = filtrarPorPeriodo(historicoCompleto);

    if (filtroOperador) {
      dadosFiltrados = dadosFiltrados.filter(h => h.operador === filtroOperador);
    }

    if (filtroTipoEntrega) {
      dadosFiltrados = dadosFiltrados.filter(h => h.tipo_entrega === filtroTipoEntrega);
    }

    setEntregasFiltradas(dadosFiltrados);

    const totalEntregas = dadosFiltrados.length;
    const entregasCompletas = dadosFiltrados.filter(h => h.tipo_entrega === 'Completa').length;
    
    const entregasParciais = dadosFiltrados.filter(h => 
      h.tipo_entrega === 'Parcial' && 
      !h.status_entrega?.toLowerCase().includes('atrasado')
    ).length;
    
    const entregasAtrasadas = dadosFiltrados.filter(h => 
      h.tipo_entrega === 'Atrasada' || 
      (h.tipo_entrega === 'Parcial' && h.status_entrega?.toLowerCase().includes('atrasado'))
    ).length;
    
    const entregasAtrasadasParciais = dadosFiltrados.filter(h => h.tipo_entrega === 'Atrasada e Parcial').length;
    
    const entregasEmDia = dadosFiltrados.filter(h => 
      h.status_entrega && h.status_entrega.toLowerCase().includes('em dia')
    ).length;
    const entregasComAtraso = dadosFiltrados.filter(h => 
      h.status_entrega && h.status_entrega.toLowerCase().includes('atrasado')
    ).length;

    const pendenciasNaoResolvidas = dadosFiltrados.filter(h => 
      (h.tipo_entrega === 'Parcial' || h.tipo_entrega === 'Atrasada e Parcial') && 
      h.resolvido?.toLowerCase() !== 'sim'
    ).map(h => {
      let diasPendente = 0;
      if (h.data_hoje) {
        const partes = h.data_hoje.split(' ')[0].split('/');
        if (partes.length === 3) {
          const dataCriacao = new Date(
            parseInt(partes[2]),
            parseInt(partes[1]) - 1,
            parseInt(partes[0])
          );
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          dataCriacao.setHours(0, 0, 0, 0);
          diasPendente = Math.floor((hoje - dataCriacao) / (1000 * 60 * 60 * 24));
        }
      }
      return { ...h, diasPendente };
    }).sort((a, b) => b.diasPendente - a.diasPendente);

    const resolvidasPorOperador = {};
    dadosFiltrados.forEach(h => {
      if ((h.tipo_entrega === 'Parcial' || h.tipo_entrega === 'Atrasada e Parcial') && 
          h.resolvido?.toLowerCase() === 'sim') {
        const op = h.operador || 'Não identificado';
        if (!resolvidasPorOperador[op]) {
          resolvidasPorOperador[op] = {
            operador: op,
            resolvidas: 0,
            naoResolvidas: 0
          };
        }
        resolvidasPorOperador[op].resolvidas++;
      }
    });

    pendenciasNaoResolvidas.forEach(h => {
      const op = h.operador || 'Não identificado';
      if (!resolvidasPorOperador[op]) {
        resolvidasPorOperador[op] = {
          operador: op,
          resolvidas: 0,
          naoResolvidas: 0
        };
      }
      resolvidasPorOperador[op].naoResolvidas++;
    });

    const resolvidasArray = Object.values(resolvidasPorOperador).map(op => ({
      ...op,
      total: op.resolvidas + op.naoResolvidas,
      taxaResolucao: (op.resolvidas + op.naoResolvidas) > 0 
        ? ((op.resolvidas / (op.resolvidas + op.naoResolvidas)) * 100).toFixed(1) 
        : 0
    })).sort((a, b) => b.total - a.total);

    setPendenciasResolvidasPorOperador(resolvidasArray);

    let somaDiasAtraso = 0;
    let countAtraso = 0;
    dadosFiltrados.forEach(h => {
      if (h.status_entrega) {
        const match = h.status_entrega.match(/\((\d+)\s*dias?\)/i);
        if (match && h.status_entrega.toLowerCase().includes('atrasado')) {
          somaDiasAtraso += parseInt(match[1]);
          countAtraso++;
        }
      }
    });
    const mediaDiasAtraso = countAtraso > 0 ? (somaDiasAtraso / countAtraso).toFixed(1) : 0;

    setEstatisticasGerais({
      totalEntregas,
      entregasCompletas,
      entregasParciais,
      entregasAtrasadas,
      entregasAtrasadasParciais,
      entregasEmDia,
      entregasComAtraso,
      pendenciasNaoResolvidas: pendenciasNaoResolvidas.length,
      mediaDiasAtraso,
      taxaEntregaEmDia: totalEntregas > 0 ? ((entregasEmDia / totalEntregas) * 100).toFixed(1) : 0,
      taxaEntregaCompleta: totalEntregas > 0 ? ((entregasCompletas / totalEntregas) * 100).toFixed(1) : 0
    });

    setPendenciasParciais(pendenciasNaoResolvidas);

    const estatOperadores = {};
    dadosFiltrados.forEach(h => {
      const op = h.operador || 'Não identificado';
      if (!estatOperadores[op]) {
        estatOperadores[op] = {
          operador: op,
          total: 0,
          completas: 0,
          parciais: 0,
          atrasadas: 0,
          atrasadasParciais: 0,
          emDia: 0,
          comAtraso: 0,
          pendenciasNaoResolvidas: 0,
          diasAtrasoTotal: 0,
          countAtraso: 0
        };
      }
      
      estatOperadores[op].total++;
      
      if (h.tipo_entrega === 'Completa') estatOperadores[op].completas++;
      
      if (h.tipo_entrega === 'Parcial') {
        if (h.status_entrega?.toLowerCase().includes('atrasado')) {
          estatOperadores[op].atrasadas++;
        } else {
          estatOperadores[op].parciais++;
        }
      }
      
      if (h.tipo_entrega === 'Atrasada') estatOperadores[op].atrasadas++;
      if (h.tipo_entrega === 'Atrasada e Parcial') estatOperadores[op].atrasadasParciais++;
      
      if (h.status_entrega?.toLowerCase().includes('em dia')) estatOperadores[op].emDia++;
      if (h.status_entrega?.toLowerCase().includes('atrasado')) estatOperadores[op].comAtraso++;
      
      if ((h.tipo_entrega === 'Parcial' || h.tipo_entrega === 'Atrasada e Parcial') && 
          h.resolvido?.toLowerCase() !== 'sim') {
        estatOperadores[op].pendenciasNaoResolvidas++;
      }

      if (h.status_entrega) {
        const match = h.status_entrega.match(/\((\d+)\s*dias?\)/i);
        if (match && h.status_entrega.toLowerCase().includes('atrasado')) {
          estatOperadores[op].diasAtrasoTotal += parseInt(match[1]);
          estatOperadores[op].countAtraso++;
        }
      }
    });

    const estatOperadoresArray = Object.values(estatOperadores).map(op => ({
      ...op,
      taxaEmDia: op.total > 0 ? ((op.emDia / op.total) * 100).toFixed(1) : 0,
      taxaCompleta: op.total > 0 ? ((op.completas / op.total) * 100).toFixed(1) : 0,
      mediaDiasAtraso: op.countAtraso > 0 ? (op.diasAtrasoTotal / op.countAtraso).toFixed(1) : 0
    })).sort((a, b) => b.total - a.total);

    setEstatisticasPorOperador(estatOperadoresArray);

    const evolucaoMensalMap = {};
    dadosFiltrados.forEach(h => {
      if (!h.data_hoje) return;
      
      const partes = h.data_hoje.split(' ')[0].split('/');
      if (partes.length !== 3) return;
      
      const mesAno = `${partes[1]}/${partes[2]}`;
      
      if (!evolucaoMensalMap[mesAno]) {
        evolucaoMensalMap[mesAno] = {
          mes: mesAno,
          total: 0,
          completas: 0,
          parciais: 0,
          atrasadas: 0,
          emDia: 0
        };
      }
      
      evolucaoMensalMap[mesAno].total++;
      if (h.tipo_entrega === 'Completa') evolucaoMensalMap[mesAno].completas++;
      
      if (h.tipo_entrega === 'Parcial') {
        if (h.status_entrega?.toLowerCase().includes('atrasado')) {
          evolucaoMensalMap[mesAno].atrasadas++;
        } else {
          evolucaoMensalMap[mesAno].parciais++;
        }
      }
      
      if (h.tipo_entrega === 'Atrasada') {
        evolucaoMensalMap[mesAno].atrasadas++;
      }
      
      if (h.tipo_entrega === 'Atrasada e Parcial') {
        evolucaoMensalMap[mesAno].parciais++;
        evolucaoMensalMap[mesAno].atrasadas++;
      }
      
      if (h.status_entrega?.toLowerCase().includes('em dia')) {
        evolucaoMensalMap[mesAno].emDia++;
      }
    });

    const evolucaoArray = Object.values(evolucaoMensalMap).sort((a, b) => {
      const [mesA, anoA] = a.mes.split('/').map(Number);
      const [mesB, anoB] = b.mes.split('/').map(Number);
      return anoA !== anoB ? anoA - anoB : mesA - mesB;
    });

    setEvolucaoMensal(evolucaoArray);

    const calcularInsightsIA = () => {
      const totalEntregasCalc = dadosFiltrados.length;
      if (totalEntregasCalc === 0) return {};

      const entregasCompletasEmDia = dadosFiltrados.filter(h => 
        h.tipo_entrega === 'Completa' && 
        h.status_entrega?.toLowerCase().includes('em dia')
      ).length;
      const taxaSucesso = ((entregasCompletasEmDia / totalEntregasCalc) * 100).toFixed(1);

      let melhorOperador = null;
      let piorOperador = null;
      if (estatOperadoresArray.length > 0) {
        const operadoresComEntregas = estatOperadoresArray.filter(op => op.total >= 3);
        if (operadoresComEntregas.length > 0) {
          melhorOperador = operadoresComEntregas.reduce((a, b) => 
            parseFloat(a.taxaEmDia) > parseFloat(b.taxaEmDia) ? a : b
          );
          piorOperador = operadoresComEntregas.reduce((a, b) => 
            parseFloat(a.taxaEmDia) < parseFloat(b.taxaEmDia) ? a : b
          );
        }
      }

      let tendenciaMelhoria = 0;
      if (evolucaoArray.length >= 2) {
        const ultimoMes = evolucaoArray[evolucaoArray.length - 1];
        const penultimoMes = evolucaoArray[evolucaoArray.length - 2];
        if (penultimoMes.total > 0 && ultimoMes.total > 0) {
          const taxaUltimo = (ultimoMes.completas / ultimoMes.total) * 100;
          const taxaPenultimo = (penultimoMes.completas / penultimoMes.total) * 100;
          tendenciaMelhoria = taxaUltimo - taxaPenultimo;
        }
      }

      const mediaResolucaoDiaria = pendenciasNaoResolvidas.length > 0 ? 
        (resolvidasArray.reduce((acc, op) => acc + op.resolvidas, 0) / 30) : 0;
      const diasParaZerarPendencias = mediaResolucaoDiaria > 0 ? 
        Math.ceil(pendenciasNaoResolvidas.length / mediaResolucaoDiaria) : 999;

      const entregasPorDiaSemana = {};
      dadosFiltrados.forEach(h => {
        if (h.data_hoje) {
          const partes = h.data_hoje.split(' ')[0].split('/');
          if (partes.length === 3) {
            const data = new Date(parseInt(partes[2]), parseInt(partes[1]) - 1, parseInt(partes[0]));
            const diaSemana = data.toLocaleDateString('pt-BR', { weekday: 'long' });
            entregasPorDiaSemana[diaSemana] = (entregasPorDiaSemana[diaSemana] || 0) + 1;
          }
        }
      });
      const diaMaisAtivo = Object.entries(entregasPorDiaSemana).length > 0 ?
        Object.entries(entregasPorDiaSemana).reduce((a, b) => a[1] > b[1] ? a : b)[0] : 'N/A';

      return {
        taxaSucesso,
        melhorOperador,
        piorOperador,
        tendenciaMelhoria: tendenciaMelhoria.toFixed(1),
        diasParaZerarPendencias,
        diaMaisAtivo,
        mediaEntregasDia: (totalEntregasCalc / 30).toFixed(1),
        entregasPorDiaSemana
      };
    };

    setInsightsIA(calcularInsightsIA());

    const ranking = estatOperadoresArray
      .filter(op => op.total >= 2)
      .map(op => ({
        ...op,
        score: (
          (parseFloat(op.taxaEmDia) * 0.4) + 
          (parseFloat(op.taxaCompleta) * 0.3) + 
          ((100 - (op.pendenciasNaoResolvidas * 10)) * 0.2) +
          ((10 - Math.min(parseFloat(op.mediaDiasAtraso), 10)) * 0.1)
        ).toFixed(1)
      }))
      .sort((a, b) => parseFloat(b.score) - parseFloat(a.score))
      .slice(0, 5);
    
    setRankingOperadores(ranking);

    const evolucaoSemanalMap = {};
    dadosFiltrados.forEach(h => {
      if (h.data_hoje) {
        const partes = h.data_hoje.split(' ')[0].split('/');
        if (partes.length === 3) {
          const data = new Date(parseInt(partes[2]), parseInt(partes[1]) - 1, parseInt(partes[0]));
          const inicioSemana = new Date(data);
          inicioSemana.setDate(data.getDate() - data.getDay());
          const chaveSemana = inicioSemana.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          
          if (!evolucaoSemanalMap[chaveSemana]) {
            evolucaoSemanalMap[chaveSemana] = {
              semana: `Sem. ${chaveSemana}`,
              total: 0,
              completas: 0,
              atrasadas: 0,
              parciais: 0
            };
          }
          
          evolucaoSemanalMap[chaveSemana].total++;
          if (h.tipo_entrega === 'Completa') evolucaoSemanalMap[chaveSemana].completas++;
          if (h.tipo_entrega === 'Atrasada' || h.status_entrega?.toLowerCase().includes('atrasado')) {
            evolucaoSemanalMap[chaveSemana].atrasadas++;
          }
          if (h.tipo_entrega === 'Parcial' || h.tipo_entrega === 'Atrasada e Parcial') {
            evolucaoSemanalMap[chaveSemana].parciais++;
          }
        }
      }
    });

    const evolucaoSemanalArray = Object.values(evolucaoSemanalMap)
      .sort((a, b) => {
        const [diaA, mesA] = a.semana.replace('Sem. ', '').split('/').map(Number);
        const [diaB, mesB] = b.semana.replace('Sem. ', '').split('/').map(Number);
        return mesA !== mesB ? mesA - mesB : diaA - diaB;
      })
      .slice(-4);

    setEvolucaoSemanal(evolucaoSemanalArray);

    const calcularTendencias = () => {
      if (evolucaoArray.length < 2) return {};

      const ultimoMes = evolucaoArray[evolucaoArray.length - 1];
      const penultimoMes = evolucaoArray[evolucaoArray.length - 2];

      const tendenciaTotal = penultimoMes.total > 0 ? 
        (((ultimoMes.total - penultimoMes.total) / penultimoMes.total) * 100).toFixed(1) : 0;
      
      const tendenciaCompletas = penultimoMes.completas > 0 ? 
        (((ultimoMes.completas - penultimoMes.completas) / penultimoMes.completas) * 100).toFixed(1) : 0;

      const tendenciaAtrasadas = penultimoMes.atrasadas > 0 ? 
        (((ultimoMes.atrasadas - penultimoMes.atrasadas) / penultimoMes.atrasadas) * 100).toFixed(1) : 0;

      return {
        total: { valor: tendenciaTotal, direcao: parseFloat(tendenciaTotal) >= 0 ? 'crescimento' : 'queda' },
        completas: { valor: tendenciaCompletas, direcao: parseFloat(tendenciaCompletas) >= 0 ? 'crescimento' : 'queda' },
        atrasadas: { valor: tendenciaAtrasadas, direcao: parseFloat(tendenciaAtrasadas) >= 0 ? 'crescimento' : 'queda' }
      };
    };

    setTendencias(calcularTendencias());

  }, [historicoCompleto, filtroOperador, filtroPeriodo, filtroTipoEntrega, filtrarPorPeriodo]);

  // Dados para gráficos
  const dadosGraficoTipoEntrega = [
    { name: 'Completa', value: estatisticasGerais.entregasCompletas || 0, color: CORES.completa },
    { name: 'Parcial', value: estatisticasGerais.entregasParciais || 0, color: CORES.parcial },
    { name: 'Atrasada', value: estatisticasGerais.entregasAtrasadas || 0, color: CORES.atrasada },
    { name: 'Atrasada e Parcial', value: estatisticasGerais.entregasAtrasadasParciais || 0, color: CORES.atrasadaParcial }
  ].filter(item => item.value > 0);

  const dadosGraficoStatus = [
    { name: 'Em Dia', value: estatisticasGerais.entregasEmDia || 0, color: CORES.emDia },
    { name: 'Com Atraso', value: estatisticasGerais.entregasComAtraso || 0, color: CORES.atrasada }
].filter(item => item.value > 0);

  // LOADING DURANTE VERIFICAÇÃO DE ACESSO
  if (carregandoAcesso) {
    return (
      <div className="gestao-contabil-page">
        <div className="container mt-5 text-center">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <div>
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Verificando acesso...</span>
              </div>
              <p className="text-muted">Verificando permissões de acesso...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TELA DE ACESSO NEGADO
  if (acessoNegado) {
    return (
      <div className="gestao-contabil-page">
        <div className="container mt-5 text-center">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <div className="alert alert-danger p-5 shadow-lg" role="alert" style={{ maxWidth: '500px', borderRadius: '16px' }}>
              <i className="fas fa-lock fa-4x mb-4 text-danger"></i>
              <h2 className="mb-3">Acesso Negado</h2>
              <p className="mb-4">
                Você não tem permissão para acessar a área de <strong>Gestão Contábil</strong>.
              </p>
              <p className="text-muted mb-3">
                Esta página é restrita apenas para usuários do grupo <strong className="text-primary">Administrador</strong>.
              </p>
              <hr />
              <div className="text-muted mb-4">
                <small>
                  <i className="fas fa-user me-2"></i>
                  Usuário: <strong>{dadosUsuario?.username || 'Não identificado'}</strong>
                </small>
                <br />
                <small>
                  <i className="fas fa-users me-2"></i>
                  Grupo: <strong>{dadosUsuario?.grupo || 'Não definido'}</strong>
                </small>
              </div>
              <button 
                className="btn btn-outline-primary btn-lg"
                onClick={() => window.location.href = '/home'}
              >
                <i className="fas fa-home me-2"></i>
                Voltar para página inicial
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar loading
  if (carregando) {
    return (
      <div className="gestao-contabil-page">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-3">Carregando dados de análise...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-contabil-page">
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="header-section mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="page-title">
                <i className="fas fa-chart-line me-3 color-primary"></i>
                Gestão Contábil
              </h1>
              <p className="page-subtitle">Análise de entregas e desempenho dos operadores</p>
            </div>
            <button 
              className="btn btn-outline-primary"
              onClick={carregarHistorico}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Atualizar Dados
            </button>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Filtros */}
        <div className="filtros-section mb-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-calendar-alt me-2 text-primary"></i>
                    Período
                  </label>
                  <select 
                    className="form-select"
                    value={filtroPeriodo}
                    onChange={(e) => setFiltroPeriodo(e.target.value)}
                  >
                    <option value="7">Últimos 7 dias</option>
                    <option value="15">Últimos 15 dias</option>
                    <option value="30">Últimos 30 dias</option>
                    <option value="60">Últimos 60 dias</option>
                    <option value="90">Últimos 90 dias</option>
                    <option value="180">Últimos 6 meses</option>
                    <option value="365">Último ano</option>
                    <option value="todos">Todo o histórico</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-user me-2 text-primary"></i>
                    Operador
                  </label>
                  <select 
                    className="form-select"
                    value={filtroOperador}
                    onChange={(e) => setFiltroOperador(e.target.value)}
                  >
                    <option value="">Todos os operadores</option>
                    {operadoresUnicos.map(op => (
                      <option key={op} value={op}>{op}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-tag me-2 text-primary"></i>
                    Tipo de Entrega
                  </label>
                  <select 
                    className="form-select"
                    value={filtroTipoEntrega}
                    onChange={(e) => setFiltroTipoEntrega(e.target.value)}
                  >
                    <option value="">Todos os tipos</option>
                    <option value="Completa">Completa</option>
                    <option value="Parcial">Parcial</option>
                    <option value="Atrasada">Atrasada</option>
                    <option value="Atrasada e Parcial">Atrasada e Parcial</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <button 
                    className="btn btn-secondary w-100"
                    onClick={() => {
                      setFiltroPeriodo('30');
                      setFiltroOperador('');
                      setFiltroTipoEntrega('');
                    }}
                  >
                    <i className="fas fa-eraser me-2"></i>
                    Limpar Filtros
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Abas de navegação */}
        <ul className="nav nav-tabs nav-tabs-custom mb-4">
                <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'status-atual' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('status-atual')}
            >
              <i className="fas fa-tachometer-alt me-2"></i>
              Status Atual
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'visaoGeral' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('visaoGeral')}
            >
              <i className="fas fa-th-large me-2"></i>
              Histórico de Entregas
            </button>
          </li>
    
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'operadores' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('operadores')}
            >
              <i className="fas fa-users me-2"></i>
              Por Operador
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'pendencias' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('pendencias')}
            >
              <i className="fas fa-exclamation-triangle me-2"></i>
              Pendências
              {estatisticasGerais.pendenciasNaoResolvidas > 0 && (
                <span className="badge bg-danger ms-2">{estatisticasGerais.pendenciasNaoResolvidas}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'evolucao' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('evolucao')}
            >
              <i className="fas fa-chart-area me-2"></i>
              Evolução
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${abaAtiva === 'inadimplencia' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('inadimplencia')}
            >
              <i className="fas fa-exclamation-circle me-2"></i>
              Inadimplência
              {empresasInadimplentes.length > 0 && (
                <span className="badge bg-danger ms-2">{empresasInadimplentes.length}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${abaAtiva === 'historico-atrasos' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('historico-atrasos')}
            >
              <i className="fas fa-clock me-2"></i>
              Histórico de Atrasos
              {historicoAtrasos.length > 0 && (
                <span className="badge bg-warning text-dark ms-2">{historicoAtrasos.length}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${abaAtiva === 'envio-fechamento' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('envio-fechamento')}
            >
              <i className="fas fa-paper-plane me-2"></i>
              Envio de Fechamento
              {historicoFechamentos.length > 0 && (
                <span className="badge bg-primary ms-2">{historicoFechamentos.length}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${abaAtiva === 'auditoria' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('auditoria')}
            >
              <i className="fas fa-search-dollar me-2"></i>
              Gestão de Auditorias
            </button>
          </li>
        </ul>

        {/* Conteúdo das Abas */}
<div className="tab-content">
          {/* Aba Visão Geral */}
          {abaAtiva === 'visaoGeral' && (
            <div className="tab-pane-custom">
              {/* Cards de KPIs */}
              <div className="row g-4 mb-4">
                <div className="col-xl-3 col-md-6">
                  <div
                    className="kpi-card kpi-total"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setModalEntregas({ show: true })}
                    title={`Ver as ${estatisticasGerais.totalEntregas || 0} entregas`}
                  >
                    <div className="kpi-icon">
                      <i className="fas fa-file-invoice"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{estatisticasGerais.totalEntregas || 0}</h3>
                      <p className="kpi-label">
                        Total de Entregas
                        <i className="fas fa-list ms-2 text-muted"></i>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="kpi-card kpi-success">
                    <div className="kpi-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{estatisticasGerais.taxaEntregaEmDia || 0}%</h3>
                      <p className="kpi-label">Taxa Em Dia</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="kpi-card kpi-warning">
                    <div className="kpi-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{estatisticasGerais.pendenciasNaoResolvidas || 0}</h3>
                      <p className="kpi-label">Pendências Abertas</p>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="kpi-card kpi-danger">
                    <div className="kpi-icon">
                      <i className="fas fa-exclamation-circle"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{estatisticasGerais.mediaDiasAtraso || 0}</h3>
                      <p className="kpi-label">Média Dias Atraso</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ANÁLISE DE TENDÊNCIAS - ESTILO IA */}
              {tendencias && Object.keys(tendencias).length > 0 && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-white">
                        <h5 className="card-title mb-0">
                          <i className="fas fa-brain me-2"></i>
                          Análise Preditiva - Comparação Mensal
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-4 text-center border-end">
                            <div className="mb-2">
                              <i className={`fas fa-arrow-${tendencias.total?.direcao === 'crescimento' ? 'up' : 'down'} fa-2x ${tendencias.total?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}></i>
                            </div>
                            <h6 className="text-muted small">TENDÊNCIA - VOLUME</h6>
                            <h4 className={`mb-1 ${tendencias.total?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}>
                              {parseFloat(tendencias.total?.valor || 0) >= 0 ? '+' : ''}{tendencias.total?.valor || 0}%
                            </h4>
                            <small className="text-muted">Variação no total de entregas</small>
                          </div>
                          
                          <div className="col-md-4 text-center border-end">
                            <div className="mb-2">
                              <i className={`fas fa-arrow-${tendencias.completas?.direcao === 'crescimento' ? 'up' : 'down'} fa-2x ${tendencias.completas?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}></i>
                            </div>
                            <h6 className="text-muted small">TENDÊNCIA - COMPLETAS</h6>
                            <h4 className={`mb-1 ${tendencias.completas?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}>
                              {parseFloat(tendencias.completas?.valor || 0) >= 0 ? '+' : ''}{tendencias.completas?.valor || 0}%
                            </h4>
                            <small className="text-muted">Entregas sem pendências</small>
                          </div>
                          
                          <div className="col-md-4 text-center">
                            <div className="mb-2">
                              <i className={`fas fa-arrow-${tendencias.atrasadas?.direcao === 'crescimento' ? 'up' : 'down'} fa-2x ${tendencias.atrasadas?.direcao === 'crescimento' ? 'text-danger' : 'text-success'}`}></i>
                            </div>
                            <h6 className="text-muted small">TENDÊNCIA - ATRASOS</h6>
                            <h4 className={`mb-1 ${tendencias.atrasadas?.direcao === 'crescimento' ? 'text-danger' : 'text-success'}`}>
                              {parseFloat(tendencias.atrasadas?.valor || 0) >= 0 ? '+' : ''}{tendencias.atrasadas?.valor || 0}%
                            </h4>
                            <small className="text-muted">
                              {tendencias.atrasadas?.direcao === 'crescimento'
                                ? <>Aumento de atrasos <i className="fas fa-exclamation-triangle text-warning ms-1" aria-hidden="true"></i></>
                                : <>Redução de atrasos <i className="fas fa-check-circle text-success ms-1" aria-hidden="true"></i></>}
                            </small>
                          </div>
                        </div>
                        
                        <hr />
                        
                        <div className="alert alert-info mb-0 border-start border-primary border-4">
                          <div className="row align-items-center">
                            <div className="col-md-9">
                              <small>
                                <strong><i className="fas fa-lightbulb text-warning me-1"></i>Insight IA:</strong>
                                {' '}
                                {parseFloat(tendencias.completas?.valor || 0) > 0 && parseFloat(tendencias.atrasadas?.valor || 0) < 0
                                  ? `Excelente! Aumento de ${tendencias.completas?.valor}% nas entregas completas e redução de ${Math.abs(tendencias.atrasadas?.valor)}% nos atrasos. Continue assim!`
                                  : parseFloat(tendencias.atrasadas?.valor || 0) > 10
                                  ? `Atenção! Aumento de ${tendencias.atrasadas?.valor}% nos atrasos. Recomendamos revisar a distribuição de clientes entre operadores.`
                                  : `Performance estável. Taxa de entregas em dia: ${estatisticasGerais.taxaEntregaEmDia}%. Meta sugerida: 85%.`
                                }
                              </small>
                            </div>
                            <div className="col-md-3 text-end">
                              <span className="badge bg-primary me-1">
                                <i className="fas fa-chart-line me-1"></i>
                                Análise IA
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Gráficos principais */}
              <div className="row g-4 mb-4">
                <div className="col-lg-6">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-pie me-2 text-primary"></i>
                        Distribuição por Tipo de Entrega
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosGraficoTipoEntrega.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={dadosGraficoTipoEntrega}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              innerRadius={60}
                              paddingAngle={3}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {dadosGraficoTipoEntrega.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Entregas']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-balance-scale me-2 text-primary"></i>
                        Status das Entregas
                      </h5>
                    </div>
                    <div className="card-body">
                      {dadosGraficoStatus.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={dadosGraficoStatus}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              innerRadius={60}
                              paddingAngle={3}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {dadosGraficoStatus.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Entregas']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* GRÁFICOS ADICIONAIS */}
              <div className="row g-4 mb-4">
                {/* Evolução Semanal */}
                <div className="col-lg-8">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-area me-2 text-success"></i>
                        Evolução Semanal - Últimas 4 Semanas
                      </h5>
                    </div>
                    <div className="card-body">
                      {evolucaoSemanal.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart data={evolucaoSemanal}>
                            <defs>
                              <linearGradient id="colorCompletas" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#28a745" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#28a745" stopOpacity={0.1}/>
                              </linearGradient>
                              <linearGradient id="colorAtrasadas" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#dc3545" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#dc3545" stopOpacity={0.1}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="semana" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Area type="monotone" dataKey="completas" name="Completas" stroke="#28a745" fill="url(#colorCompletas)" />
                            <Area type="monotone" dataKey="atrasadas" name="Atrasadas" stroke="#dc3545" fill="url(#colorAtrasadas)" />
                            <Area type="monotone" dataKey="parciais" name="Parciais" stroke="#ffc107" fill="#ffc107" fillOpacity={0.3} />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-chart-area fa-3x mb-3"></i>
                          <p>Dados insuficientes para análise semanal</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ranking de Operadores */}
                <div className="col-lg-4">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-trophy me-2 text-warning"></i>
                        Ranking - Top 5 operadores que entregam em dia
                      </h5>
                    </div>
                    <div className="card-body">
                      {rankingOperadores.length > 0 ? (
                        <div className="ranking-list">
                          {rankingOperadores.map((op, index) => (
                            <div key={op.operador} className={`ranking-item d-flex align-items-center p-2 mb-2 rounded ${index === 0 ? 'bg-warning bg-opacity-25' : 'bg-light'}`}>
                              <div className="ranking-position me-3">
                                {index === 0 ? (
                                  <i className="fas fa-crown text-warning fa-lg"></i>
                                ) : index === 1 ? (
                                  <i className="fas fa-medal text-secondary fa-lg"></i>
                                ) : index === 2 ? (
                                  <i className="fas fa-medal text-danger fa-lg" style={{color: '#cd7f32'}}></i>
                                ) : (
                                  <span className="badge bg-secondary">{index + 1}º</span>
                                )}
                              </div>
                              <div className="flex-grow-1">
                                <div className="fw-semibold">{op.operador}</div>
                                <small className="text-muted">
                                  {op.total} entregas | {op.taxaEmDia}% em dia
                                </small>
                              </div>
                              <div className="text-end">
                                <span className={`badge ${parseFloat(op.score) >= 70 ? 'bg-success' : parseFloat(op.score) >= 50 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                  {op.score} pts
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i className="fas fa-users fa-3x mb-3"></i>

<p>Dados insuficientes para ranking</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* COMPARATIVO POR OPERADOR - GRÁFICO DE BARRAS */}
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-info"></i>
                        Comparativo de Performance por Operador
                      </h5>
                    </div>
                    <div className="card-body">
                      {estatisticasPorOperador.length > 0 ? (
                        <ResponsiveContainer width="100%" height={350}>
                          <BarChart data={estatisticasPorOperador.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="operador" 
                              angle={-45} 
                              textAnchor="end" 
                              height={80}
                              interval={0}
                              tick={{ fontSize: 11 }}
                            />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="completas" name="Completas" fill={CORES.completa} />
                            <Bar dataKey="parciais" name="Parciais" fill={CORES.parcial} />
                            <Bar dataKey="atrasadas" name="Atrasadas" fill={CORES.atrasada} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-chart-bar fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* INSIGHTS E FEEDBACKS DA IA */}
              {insightsIA && Object.keys(insightsIA).length > 0 && (
                <div className="row g-4 mb-4">
                  <div className="col-12">
                    <div className="card shadow-sm border-0">
                      <div className="card-header bg-white">
                        <h5 className="card-title mb-0 text-white">
                          <i className="fas fa-robot me-2"></i>
                          Insights Inteligentes - Análise Automatizada
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row g-4">
                          {/* Card de Taxa de Sucesso */}
                          <div className="col-md-4">
                            <div className="insight-card p-3 rounded h-100" style={{background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'}}>
                              <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-bullseye fa-2x text-success me-3"></i>
                                <div>
                                  <small className="text-muted d-block">Taxa de Sucesso</small>
                                  <h4 className="mb-0 text-success">{insightsIA.taxaSucesso}%</h4>
                                </div>
                              </div>
                              <small className="text-muted">
                                Entregas completas e em dia
                              </small>
                              <div className="progress mt-2" style={{height: '8px'}}>
                                <div 
                                  className={`progress-bar ${parseFloat(insightsIA.taxaSucesso) >= 80 ? 'bg-success' : parseFloat(insightsIA.taxaSucesso) >= 60 ? 'bg-warning' : 'bg-danger'}`}
                                  style={{width: `${insightsIA.taxaSucesso}%`}}
                                ></div>
                              </div>
                            </div>
                          </div>

                          {/* Card Melhor Operador */}
                          <div className="col-md-4">
                            <div className="insight-card p-3 rounded h-100" style={{background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)'}}>
                              <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-star fa-2x text-warning me-3"></i>
                                <div>
                                  <small className="text-muted d-block">Destaque do Período</small>
                                  <h5 className="mb-0 text-success">{insightsIA.melhorOperador?.operador || 'N/A'}</h5>
                                </div>
                              </div>
                              <small className="text-muted">
                                {insightsIA.melhorOperador ? (
                                  <>
                                    <i className="fas fa-check-circle text-success me-1"></i>
                                    {insightsIA.melhorOperador.taxaEmDia}% em dia | {insightsIA.melhorOperador.total} entregas
                                  </>
                                ) : 'Dados insuficientes'}
                              </small>
                            </div>
                          </div>

                          {/* Card Projeção */}
                          <div className="col-md-4">
                            <div className="insight-card p-3 rounded h-100" style={{background: 'linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%)'}}>
                              <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-clock fa-2x text-warning me-3"></i>
                                <div>
                                  <small className="text-muted d-block">Projeção de Resolução</small>
                                  <h4 className="mb-0 text-warning">
                                    {insightsIA.diasParaZerarPendencias < 999 ? `${insightsIA.diasParaZerarPendencias} dias` : 'N/A'}
                                  </h4>
                                </div>
                              </div>
                              <small className="text-muted">
                                Para zerar pendências atuais
                              </small>
                            </div>
                          </div>
                        </div>

                        <hr />

                        {/* Feedbacks detalhados */}
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="alert alert-success mb-0">
                              <h6 className="alert-heading">
                                <i className="fas fa-thumbs-up me-2"></i>
                                Pontos Positivos
                              </h6>
                              <ul className="mb-0 small">
                                {parseFloat(estatisticasGerais.taxaEntregaEmDia) >= 70 && (
                                  <li>Taxa de entregas em dia acima de 70% <i className="fas fa-check-circle text-success ms-1" aria-hidden="true"></i></li>
                                )}
                                {parseFloat(estatisticasGerais.taxaEntregaCompleta) >= 60 && (
                                  <li>Mais de 60% das entregas são completas <i className="fas fa-check-circle text-success ms-1" aria-hidden="true"></i></li>
                                )}
                                {insightsIA.melhorOperador && parseFloat(insightsIA.melhorOperador.taxaEmDia) >= 80 && (
                                  <li>{insightsIA.melhorOperador.operador} mantém excelente performance <i className="fas fa-star ms-1" aria-hidden="true"></i></li>
                                )}
                                {parseFloat(tendencias.atrasadas?.valor || 0) < 0 && (
                                  <li>Tendência de queda nos atrasos <i className="fas fa-chart-area ms-1" aria-hidden="true"></i></li>
                                )}
                                {estatisticasGerais.pendenciasNaoResolvidas === 0 && (
                                  <li>Nenhuma pendência em aberto! </li>
                                )}
                                {parseFloat(estatisticasGerais.taxaEntregaEmDia) < 70 && parseFloat(estatisticasGerais.taxaEntregaCompleta) < 60 && (
                                  <li>Equipe mantém operação ativa</li>
                                )}
                              </ul>
                            </div>
                          </div>
                          
                          <div className="col-md-6">
                            <div className="alert alert-warning mb-0">
                              <h6 className="alert-heading">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Pontos de Atenção
                              </h6>
                              <ul className="mb-0 small">
                                {parseFloat(estatisticasGerais.taxaEntregaEmDia) < 70 && (
                                  <li>Taxa de entregas em dia abaixo de 70% <i className="fas fa-exclamation-triangle text-warning ms-1" aria-hidden="true"></i></li>
                                )}
                                {estatisticasGerais.pendenciasNaoResolvidas > 5 && (
                                  <li>{estatisticasGerais.pendenciasNaoResolvidas} pendências aguardando resolução <i className="fas fa-exclamation-triangle text-warning ms-1" aria-hidden="true"></i></li>
                                )}
                                {parseFloat(estatisticasGerais.mediaDiasAtraso) > 10 && (
                                  <li>Média de atraso acima de 10 dias <i className="fas fa-exclamation-triangle text-warning ms-1" aria-hidden="true"></i></li>
                                )}
                                {insightsIA.piorOperador && parseFloat(insightsIA.piorOperador.taxaEmDia) < 50 && (
                                  <li>{insightsIA.piorOperador.operador} precisa de suporte ({insightsIA.piorOperador.taxaEmDia}% em dia)</li>
                                )}
                                {parseFloat(tendencias.atrasadas?.valor || 0) > 10 && (
                                  <li>Tendência de aumento nos atrasos <i className="fas fa-chart-line ms-1" aria-hidden="true"></i></li>
                                )}
                                {parseFloat(estatisticasGerais.taxaEntregaEmDia) >= 70 && estatisticasGerais.pendenciasNaoResolvidas <= 5 && (
                                  <li>Continue monitorando para manter a qualidade</li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>

                        <hr />

                        {/* Estatísticas extras */}
                        <div className="row text-center g-3">
                          <div className="col-md-3">
                            <div className="p-2 bg-light rounded">
                              <i className="fas fa-calendar-day text-primary mb-1"></i>
                              <small className="d-block text-muted">Dia mais ativo</small>
                              <strong className="text-capitalize">{insightsIA.diaMaisAtivo}</strong>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-2 bg-light rounded">
                              <i className="fas fa-chart-line text-success mb-1"></i>
                              <small className="d-block text-muted">Média diária</small>
                              <strong>{insightsIA.mediaEntregasDia} entregas</strong>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-2 bg-light rounded">
                              <i className="fas fa-users text-info mb-1"></i>
                              <small className="d-block text-muted">Operadores ativos</small>
                              <strong>{operadoresUnicos.length}</strong>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="p-2 bg-light rounded">
                              <i className="fas fa-percentage text-warning mb-1"></i>
                              <small className="d-block text-muted">Tendência</small>
                              <strong className={parseFloat(insightsIA.tendenciaMelhoria) >= 0 ? 'text-success' : 'text-danger'}>
                                {parseFloat(insightsIA.tendenciaMelhoria) >= 0 ? '+' : ''}{insightsIA.tendenciaMelhoria}%
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Resumo numérico */}
              <div className="row g-4 mt-2">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-list-ol me-2 text-primary"></i>
                        Resumo Detalhado
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-3 col-6">
                          <div className="stat-item stat-success">
                            <span className="stat-value">{estatisticasGerais.entregasCompletas || 0}</span>
                            <span className="stat-label">Entregas Completas</span>
                          </div>
                        </div>
                        <div className="col-md-3 col-6">
                          <div className="stat-item stat-warning">
                            <span className="stat-value">{estatisticasGerais.entregasParciais || 0}</span>
                            <span className="stat-label">Entregas Parciais</span>
                          </div>
                        </div>
                        <div className="col-md-3 col-6">
                          <div className="stat-item stat-danger">
                            <span className="stat-value">{estatisticasGerais.entregasAtrasadas || 0}</span>
                            <span className="stat-label">Entregas Atrasadas</span>
                          </div>
                        </div>
                        <div className="col-md-3 col-6">
                          <div className="stat-item stat-orange">
                            <span className="stat-value">{estatisticasGerais.entregasAtrasadasParciais || 0}</span>
                            <span className="stat-label">Atrasadas e Parciais</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribuição Atual e Top Operadores - Status Real */}
              {statusAtual && statusAtual.total > 0 && (
                <div className="row g-4 mt-2">
                  <div className="col-lg-4">
                    <div className="card shadow-sm h-100">
                      <div className="card-header bg-white">
                        <h5 className="card-title mb-0">
                          <i className="fas fa-chart-pie me-2 text-success"></i>
                          Distribuição Atual - Em Dia vs Atrasadas
                        </h5>
                      </div>
                      <div className="card-body d-flex flex-column justify-content-center">
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Em Dia', value: statusAtual.emDia || 0, color: '#28a745' },
                                { name: 'Atrasadas', value: statusAtual.atrasadas || 0, color: '#dc3545' }
                              ]}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={90}
                              innerRadius={50}
                              paddingAngle={3}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              <Cell fill="#28a745" />
                              <Cell fill="#dc3545" />
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Empresas']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="text-center mt-2">
                          <span className="badge bg-success me-2">{statusAtual.percentualEmDia || 0}% em dia</span>
                          <span className="badge bg-danger">{statusAtual.percentualAtrasadas || 0}% atrasadas</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-8">
                    <div className="card shadow-sm h-100">
                      <div className="card-header bg-white">
                        <h5 className="card-title mb-0">
                          <i className="fas fa-chart-bar me-2 text-danger"></i>
                          Top 10 Operadores - Status Atual
                        </h5>
                      </div>
                      <div className="card-body">
                        {statusAtual.porOperador && statusAtual.porOperador.length > 0 ? (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={statusAtual.porOperador.slice(0, 10)}
                              margin={{ top: 10, right: 20, left: 10, bottom: 60 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="operador"
                                angle={-40}
                                textAnchor="end"
                                height={80}
                                interval={0}
                                tick={{ fontSize: 11 }}
                              />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="emDia" name="Em Dia" fill="#28a745" />
                              <Bar dataKey="atrasadas" name="Atrasadas" fill="#dc3545" />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="text-center text-muted py-5">
                            <i className="fas fa-chart-bar fa-3x mb-3"></i>
                            <p>Nenhum dado disponível</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Aba Status Atual */}
          {abaAtiva === 'status-atual' && (
            <div className="tab-pane-custom">
              {(carregandoStatus || carregandoInadimplencia || carregandoClientesOperador) ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="text-muted">Carregando status atual...</p>
                </div>
              ) : (
                <>
                  {/* Cards de KPIs Status Atual */}
                  <div className="row g-4 mb-4">
                    <div className="col-xl-3 col-md-6">
                      <div className="kpi-card kpi-total">
                        <div className="kpi-icon">
                          <i className="fas fa-building"></i>
                        </div>
                        <div className="kpi-content">
                          <h3 className="kpi-value">{statusAtual.total || 0}</h3>
                          <p className="kpi-label">Total de Empresas</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl-3 col-md-6">
                      <div className="kpi-card kpi-success">
                        <div className="kpi-icon">
                          <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="kpi-content">
                          <h3 className="kpi-value">{statusAtual.percentualEmDia || 0}%</h3>
                          <p className="kpi-label">Empresas Em Dia</p>
                          <small className="text-muted">{statusAtual.emDia || 0} empresas</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl-3 col-md-6">
                      <div className="kpi-card kpi-danger">
                        <div className="kpi-icon">
                          <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <div className="kpi-content">
                          <h3 className="kpi-value">{statusAtual.percentualAtrasadas || 0}%</h3>
                          <p className="kpi-label">Empresas inadimplentes</p>
                          <small className="text-muted">{statusAtual.atrasadas || 0} empresas</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl-3 col-md-6">
                      <div className="kpi-card kpi-warning">
                        <div className="kpi-icon"><i className="fas fa-exclamation-circle"></i></div>
                        <div className="kpi-content">
                          <h3 className="kpi-value">{empresasInadimplentes.length}</h3>
                          <p className="kpi-label">Inadimplentes (+10 dias)</p>
                          <small className="text-muted">
                            {empresasInadimplentes.filter(e => !e.observacao || e.observacao.trim() === '').length} sem justificativa
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Barra de Saúde da Contabilidade */}
                  <div className="card shadow-sm mb-4">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-heartbeat me-2 text-danger"></i>
                        Saúde Geral da Contabilidade — Situação Atual
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="fw-semibold text-success">
                          <i className="fas fa-check-circle me-1"></i>
                          Em Dia: {statusAtual.percentualEmDia || 0}% ({statusAtual.emDia || 0} empresas)
                        </span>
                        <span className="fw-semibold text-danger">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          Atrasadas: {statusAtual.percentualAtrasadas || 0}% ({statusAtual.atrasadas || 0} empresas)
                        </span>
                      </div>
                      <div className="progress mb-4" style={{ height: '32px', borderRadius: '16px' }}>
                        <div className="progress-bar bg-success fw-bold" style={{ width: `${statusAtual.percentualEmDia || 0}%`, fontSize: '15px' }}>
                          {parseFloat(statusAtual.percentualEmDia || 0) > 12 ? `${statusAtual.percentualEmDia}%` : ''}
                        </div>
                        <div className="progress-bar bg-danger fw-bold" style={{ width: `${statusAtual.percentualAtrasadas || 0}%`, fontSize: '15px' }}>
                          {parseFloat(statusAtual.percentualAtrasadas || 0) > 12 ? `${statusAtual.percentualAtrasadas}%` : ''}
                        </div>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-4 text-center">
                          <div className="p-3 bg-danger bg-opacity-10 rounded border border-danger">
                            <h4 className="text-danger mb-1 fw-bold">{empresasInadimplentes.length}</h4>
                            <small className="fw-semibold text-muted">Inadimplentes (+10 dias)</small>
                          </div>
                        </div>
                        <div className="col-md-4 text-center">
                          <div className="p-3 bg-warning bg-opacity-10 rounded border border-warning">
                            <h4 className="text-warning mb-1 fw-bold">
                              {empresasInadimplentes.filter(e => !e.observacao || e.observacao.trim() === '').length}
                            </h4>
                            <small className="fw-semibold text-muted">Sem Justificativa</small>
                          </div>
                        </div>
                        <div className="col-md-4 text-center">
                          <div className="p-3 bg-success bg-opacity-10 rounded border border-success">
                            <h4 className="text-success mb-1 fw-bold">
                              {empresasInadimplentes.filter(e => e.observacao && e.observacao.trim() !== '').length}
                            </h4>
                            <small className="fw-semibold text-muted">Com Justificativa</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top 3 Principais Impedimentos */}
                  {empresasInadimplentes.filter(emp => emp.observacao && emp.observacao.trim() !== '').length > 0 && (
                    <div className="card shadow-sm mb-4">
                      <div className="card-header bg-white">
                        <h5 className="card-title mb-0">
                          <i className="fas fa-lightbulb me-2 text-warning"></i>
                          Principais Impedimentos — Por que estão em atraso?
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          {(() => {
                            const categorias = [
                              { nome: 'Sem retorno do cliente', icone: 'fas fa-phone-slash', cor: 'danger', palavras: ['sem retorno', 'nao retornou', 'nao responde', 'retorno cliente', 'nao atende', 'cliente nao', 'nao conseguimos contato', 'nao consegui contato', 'sem contato', 'nao foi possivel contato', 'aguardando retorno'] },
                              { nome: 'Documentação não recebida', icone: 'fas fa-file-alt', cor: 'warning', palavras: ['documentac', 'documento', 'nao recebemos', 'nao enviou', 'nao mandou', 'nada de doc', 'sem doc', 'nao nos enviou', 'nao nos mandou', 'pendente de doc', 'falta doc', 'aguardando doc', 'nao recebeu', 'nao recebemos nada'] },
                              { nome: 'Cobrança enviada / Aguardando', icone: 'fas fa-paper-plane', cor: 'info', palavras: ['cobran', 'enviei', 'enviamos', 'lembrete', 'follow', 'acompanhando', 'aguardando resposta', 'notificac', 'notificamos', 'mandei mensagem', 'mandamos mensagem'] },
                              { nome: 'Férias / Afastamento', icone: 'fas fa-umbrella-beach', cor: 'secondary', palavras: ['ferias', 'viagem', 'afastamento', 'licenca', 'ausente', 'recesso', 'folga'] },
                              { nome: 'Problema / Pendência Interna', icone: 'fas fa-exclamation-triangle', cor: 'orange', palavras: ['problema', 'pendencia', 'erro', 'sistema', 'interno', 'bloqueio', 'impedimento', 'dificuldade'] },
                              { nome: 'Aguardando prazo / Combinado', icone: 'fas fa-clock', cor: 'primary', palavras: ['aguardando', 'esperando', 'prazo', 'combinado', 'acordado', 'prometeu', 'prometeram', 'vai enviar', 'vai mandar'] },
                            ];
                            const normalize = str => str.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
                            const totalComObs = empresasInadimplentes.filter(e => e.observacao && e.observacao.trim() !== '').length;
                            const resultado = categorias.map(cat => {
                              const normPalavras = cat.palavras.map(normalize);
                              const matches = empresasInadimplentes.filter(emp => {
                                if (!emp.observacao || emp.observacao.trim() === '') return false;
                                const texto = normalize(emp.observacao);
                                return normPalavras.some(p => texto.includes(p));
                              });
                              return { ...cat, count: matches.length, exemplos: matches.slice(0, 2).map(e => e.observacao) };
                            }).filter(c => c.count > 0).sort((a, b) => b.count - a.count).slice(0, 3);
                            const coresBorda = ['danger', 'warning', 'info'];
                            if (resultado.length === 0) return (
                              <div className="col-12 text-center text-muted py-3">
                                <i className="fas fa-search me-2"></i>Não foi possível categorizar as justificativas.
                              </div>
                            );
                            return resultado.map((cat, index) => {
                              const pct = totalComObs > 0 ? ((cat.count / totalComObs) * 100).toFixed(0) : 0;
                              const corBorda = coresBorda[index] || cat.cor;
                              return (
                                <div key={index} className="col-md-4">
                                  <div className={`border-start border-4 border-${corBorda} rounded p-3 bg-light h-100`}>
                                    <div className="d-flex align-items-center mb-2">
                                      <i className={`${cat.icone} text-${corBorda} me-2 fa-lg`}></i>
                                      <strong className="small flex-grow-1">{cat.nome}</strong>
                                      <span className={`badge bg-${corBorda}`}>{cat.count} empresa{cat.count !== 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="progress mb-1" style={{ height: '6px' }}>
                                      <div className={`progress-bar bg-${corBorda}`} style={{ width: `${pct}%` }}></div>
                                    </div>
                                    <small className="text-muted d-block mb-2">{pct}% das empresas com justificativa</small>
                                    {cat.exemplos.length > 0 && (
                                      <div className="border-top pt-2 mt-1">
                                        <small className="text-muted fw-semibold d-block mb-1">Exemplos:</small>
                                        {cat.exemplos.map((ex, i) => (
                                          <div key={i} className="small text-muted fst-italic mb-1">
                                            "{ex.length > 90 ? ex.substring(0, 90) + '…' : ex}"
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Inadimplência consolidada por Operador */}
                  <div className="card shadow-sm mb-4">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-users me-2 text-danger"></i>
                        Inadimplência por Operador — Situação Atual
                      </h5>
                      <span className="badge bg-danger fs-6">{empresasInadimplentes.length} inadimplente(s)</span>
                    </div>
                    <div className="card-body p-0">
                      {empresasInadimplentes.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="table-dark">
                              <tr>
                                <th>#</th>
                                <th>Operador</th>
                                <th className="text-center">Total Empresas</th>
                                <th className="text-center">Inadimplentes</th>
                                <th className="text-center">% Inadimp.</th>
                                <th className="text-center">Sem Justif.</th>
                                <th className="text-center">Com Justif.</th>
                                <th>Urgência</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                // agrupa inadimplentes por operador
                                const byOp = {};
                                empresasInadimplentes.forEach(emp => {
                                  const op = emp.operador || 'Não identificado';
                                  if (!byOp[op]) byOp[op] = { operador: op, inadimplentes: 0, semJustif: 0, comJustif: 0 };
                                  byOp[op].inadimplentes++;
                                  if (!emp.observacao || emp.observacao.trim() === '') byOp[op].semJustif++;
                                  else byOp[op].comJustif++;
                                });
                                // pega total de empresas por operador via statusAtual.porOperador
                                const totalPorOp = {};
                                if (statusAtual.porOperador) {
                                  statusAtual.porOperador.forEach(op => { totalPorOp[op.operador] = op.total; });
                                }
                                return Object.values(byOp)
                                  .sort((a, b) => b.inadimplentes - a.inadimplentes)
                                  .map((op, index) => {
                                    const totalEmpresas = totalPorOp[op.operador] || op.inadimplentes;
                                    const pctInadimp = totalEmpresas > 0 ? ((op.inadimplentes / totalEmpresas) * 100).toFixed(0) : 0;
                                    const pctSemJustif = op.inadimplentes > 0 ? ((op.semJustif / op.inadimplentes) * 100).toFixed(0) : 0;
                                    return (
                                      <tr key={op.operador} className={index === 0 ? 'table-danger' : ''}>
                                        <td>
                                          <span className={`badge ${index === 0 ? 'bg-danger' : index === 1 ? 'bg-warning text-dark' : index === 2 ? 'bg-secondary' : 'bg-light text-dark'}`}>
                                            {index + 1}º
                                          </span>
                                        </td>
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <div className="operador-avatar me-2" style={{ backgroundColor: CORES_OPERADORES[index % CORES_OPERADORES.length] }}>
                                              {op.operador.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="fw-semibold">{op.operador}</span>
                                          </div>
                                        </td>
                                        <td className="text-center">
                                          <span className="badge bg-primary fs-6">{totalEmpresas}</span>
                                        </td>
                                        <td className="text-center">
                                          <span className="badge bg-danger fs-6">{op.inadimplentes}</span>
                                        </td>
                                        <td className="text-center">
                                          <span className={`badge fs-6 ${parseFloat(pctInadimp) > 30 ? 'bg-danger' : parseFloat(pctInadimp) > 15 ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                            {pctInadimp}%
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <span className={`badge fs-6 ${op.semJustif > 0 ? 'bg-warning text-dark' : 'bg-success'}`}>
                                            {op.semJustif}
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <span className="badge bg-success fs-6">{op.comJustif}</span>
                                        </td>
                                        <td style={{ minWidth: '130px' }}>
                                          <div className="progress mb-1" style={{ height: '14px' }}>
                                            <div className="progress-bar bg-warning" title="Sem justificativa" style={{ width: `${pctSemJustif}%` }}>
                                              {pctSemJustif > 20 ? `${pctSemJustif}%` : ''}
                                            </div>
                                            <div className="progress-bar bg-success" title="Com justificativa" style={{ width: `${100 - pctSemJustif}%` }}></div>
                                          </div>
                                          <small className="text-muted">{pctSemJustif}% sem justif.</small>
                                        </td>
                                      </tr>
                                    );
                                  });
                              })()}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                          <p className="text-success fw-bold">Nenhum inadimplente!</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Próximas Entregas Lucro Real — 10 dias */}
                  {(() => {
                    const empresasDetalhadas = statusAtual.empresasDetalhadas || [];

                    const parseDatePE = (str) => {
                      if (!str || str === '-') return null;
                      if (str.includes('/')) {
                        const p = str.split('/');
                        if (p.length === 3) {
                          let ano = parseInt(p[2]);
                          if (ano < 100) ano = ano < 50 ? 2000 + ano : 1900 + ano;
                          const d = new Date(ano, parseInt(p[1]) - 1, parseInt(p[0]));
                          return isNaN(d.getTime()) ? null : d;
                        }
                      }
                      if (str.includes('-')) {
                        const d = new Date(str);
                        return isNaN(d.getTime()) ? null : d;
                      }
                      return null;
                    };

                    const hoje = new Date();
                    hoje.setHours(0, 0, 0, 0);

                    const lucroReal = empresasDetalhadas
                      .filter(emp => {
                        const regime = (emp.regime || '').toLowerCase();
                        if (!regime.includes('lucro real')) return false;
                        const data = parseDatePE(emp.proxima_entrega);
                        if (!data) return false;
                        const diff = Math.ceil((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
                        return diff >= 0 && diff <= 10;
                      })
                      .map(emp => {
                        const data = parseDatePE(emp.proxima_entrega);
                        const diff = Math.ceil((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
                        return { ...emp, diasRestantes: diff };
                      })
                      .sort((a, b) => a.diasRestantes - b.diasRestantes);

                    if (lucroReal.length === 0) return null;

                    const urgentes = lucroReal.filter(e => e.diasRestantes <= 5);
                    const atencao  = lucroReal.filter(e => e.diasRestantes > 5);
                    const corFaixa = (d) => d <= 5 ? 'danger' : 'warning';
                    const labelFaixa = (d) => d === 0 ? 'hoje' : `${d}d`;

                    return (
                      <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white d-flex align-items-center justify-content-between">
                          <h5 className="card-title mb-0">
                            <i className="fas fa-calendar-alt me-2 text-primary"></i>
                            Próximas Entregas — Lucro Real (próximos 10 dias)
                          </h5>
                          <div className="d-flex gap-2">
                            <span className="badge bg-danger">{urgentes.length} urgentes ≤5d</span>
                            <span className="badge bg-warning text-dark">{atencao.length} atenção 6–10d</span>
                          </div>
                        </div>
                        <div className="card-body p-0">
                          <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Empresa</th>
                                  <th>Operador</th>
                                  <th>Regime</th>
                                  <th className="text-center">Próxima Entrega</th>
                                  <th className="text-center">Faltam</th>
                                  <th>Status Contábil</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lucroReal.map((emp, index) => (
                                  <tr key={index} className={emp.diasRestantes <= 5 ? 'table-danger' : 'table-warning'}>
                                    <td className="fw-semibold">
                                      <i className="fas fa-building me-2 text-primary"></i>
                                      {emp.empresa}
                                    </td>
                                    <td><span className="badge bg-info">{emp.operador || '-'}</span></td>
                                    <td><span className="badge bg-primary">{emp.regime}</span></td>
                                    <td className="text-center fw-semibold">{emp.proxima_entrega}</td>
                                    <td className="text-center">
                                      <span className={`badge bg-${corFaixa(emp.diasRestantes)} fs-6`}>
                                        {labelFaixa(emp.diasRestantes)}
                                      </span>
                                    </td>
                                    <td>
                                      <small className={emp.Status_contabil && emp.Status_contabil.toLowerCase().includes('atrasado') ? 'text-danger fw-semibold' : 'text-success'}>
                                        {emp.Status_contabil || '-'}
                                      </small>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Empresas críticas ordenadas por dias de atraso */}
                  {empresasInadimplentes.length > 0 && (
                    <div className="card shadow-sm">
                      <div className="card-header bg-white d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">
                          <i className="fas fa-fire me-2 text-danger"></i>
                          Empresas Críticas — Maior Atraso Atual
                        </h5>
                        <span className="badge bg-danger fs-6">{empresasInadimplentes.length} empresa(s)</span>
                      </div>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="table-danger">
                              <tr>
                                <th>Empresa</th>
                                <th>Operador</th>
                                <th className="text-center">Dias Atraso</th>
                                <th>Próxima Entrega</th>
                                <th>Competência</th>
                                <th>Justificativa</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...empresasInadimplentes]
                                .sort((a, b) => (b.dias_atraso || 0) - (a.dias_atraso || 0))
                                .map((emp, index) => (
                                <tr key={index} className={!emp.observacao || emp.observacao.trim() === '' ? 'table-warning' : ''}>
                                  <td className="fw-semibold">
                                    <i className="fas fa-building me-2 text-danger"></i>
                                    {emp.empresa}
                                  </td>
                                  <td><span className="badge bg-info">{emp.operador || '-'}</span></td>
                                  <td className="text-center">
                                    <span className={`badge fs-6 ${(emp.dias_atraso || 0) > 30 ? 'bg-danger' : (emp.dias_atraso || 0) > 15 ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                      {emp.dias_atraso || 0} dias
                                    </span>
                                  </td>
                                  <td>{emp.proxima_entrega || '-'}</td>
                                  <td><span className="badge bg-dark">{emp.competencia || '-'}</span></td>
                                  <td>
                                    {emp.observacao && emp.observacao.trim() !== '' ? (
                                      <small className="text-success">
                                        <i className="fas fa-check-circle me-1"></i>
                                        {emp.observacao.length > 45 ? emp.observacao.substring(0, 45) + '...' : emp.observacao}
                                      </small>
                                    ) : (
                                      <span className="badge bg-warning text-dark">
                                        <i className="fas fa-exclamation me-1"></i>
                                        Sem justificativa
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Aba Por Operador */}
          {abaAtiva === 'operadores' && (
            <div className="tab-pane-custom">
              
              {/* NOVO GRÁFICO - Clientes Inadimplentes por Operador */}
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-users me-2 text-danger"></i>
                        Clientes Inadimplentes por Operador (+10 dias)
                      </h5>
                      <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={carregarClientesPorOperador}
                        disabled={carregandoClientesOperador}
                      >
                        <i className={`fas fa-sync-alt me-2 ${carregandoClientesOperador ? 'fa-spin' : ''}`}></i>
                        Atualizar
                      </button>
                    </div>
                    <div className="card-body">
                      {carregandoClientesOperador ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Carregando...</span>
                          </div>
                          <p className="text-muted">Carregando dados de inadimplência...</p>
                        </div>
                      ) : clientesPorOperador.length > 0 ? (
                        <div className="row">
                          {/* Gráfico de Barras */}
                          <div className="col-lg-8">
                            <ResponsiveContainer width="100%" height={400}>
                              <BarChart 
                                data={clientesPorOperador} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                  dataKey="operador" 
                                  angle={-45} 
                                  textAnchor="end" 
                                  height={100}
                                  interval={0}
                                  tick={{ fontSize: 12 }}
                                />
                                <YAxis />
                                <Tooltip 
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-white border border-secondary rounded p-3 shadow-sm">
                                          <p className="mb-2 fw-bold">{data.operador}</p>
                                          <p className="mb-1 text-danger">
                                            <i className="fas fa-exclamation-circle me-2"></i>
                                            <strong>Inadimplentes:</strong> {data.comAtraso}
                                          </p>
                                          <p className="mb-1 text-warning">
                                            <i className="fas fa-comment-slash me-2"></i>
                                            <strong>Sem Justificativa:</strong> {data.semObservacao}
                                          </p>
                                          <p className="mb-0 text-success">
                                            <i className="fas fa-comment-dots me-2"></i>
                                            <strong>Com Justificativa:</strong> {data.comAtraso - data.semObservacao}
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Legend />
                                <Bar 
                                  dataKey="comAtraso" 
                                  name="Clientes Inadimplentes" 
                                  fill="#dc3545"
                                  radius={[8, 8, 0, 0]}
                                />
                                <Bar 
                                  dataKey="semObservacao" 
                                  name="Sem Justificativa" 
                                  fill="#ffc107"
                                  radius={[8, 8, 0, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>

                          {/* Cards de Resumo */}
                          <div className="col-lg-4">
                            <div className="card bg-light border-0 mb-3">
                              <div className="card-body">
                                <h6 className="text-muted mb-3">
                                  <i className="fas fa-chart-bar me-2"></i>
                                  Resumo Geral
                                </h6>
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                                  <span className="text-muted">Total de Operadores:</span>
                                  <span className="badge bg-primary fs-6">{clientesPorOperador.length}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                                  <span className="text-muted">Total Inadimplentes:</span>
                                  <span className="badge bg-danger fs-6">
                                    {clientesPorOperador.reduce((acc, op) => acc + op.comAtraso, 0)}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                                  <span className="text-muted">Sem Justificativa:</span>
                                  <span className="badge bg-warning text-dark fs-6">
                                    {clientesPorOperador.reduce((acc, op) => acc + op.semObservacao, 0)}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted">Com Justificativa:</span>
                                  <span className="badge bg-success fs-6">
                                    {clientesPorOperador.reduce((acc, op) => acc + (op.comAtraso - op.semObservacao), 0)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Top 3 Operadores com mais inadimplentes */}
                            <div className="card bg-danger bg-opacity-10 border-danger">
                              <div className="card-body">
                                <h6 className="text-danger mb-3">
                                  <i className="fas fa-exclamation-triangle me-2"></i>
                                  Top 3 - Mais Inadimplentes
                                </h6>
                                {clientesPorOperador.slice(0, 3).map((op, index) => (
                                  <div 
                                    key={op.operador} 
                                    className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
                                  >
                                    <div className="d-flex align-items-center">
                                      <span className={`badge ${
                                        index === 0 ? 'bg-danger' : 
                                        index === 1 ? 'bg-warning text-dark' : 
                                        'bg-secondary'
                                      } me-2`}>
                                        {index + 1}º
                                      </span>
                                      <span className="fw-semibold">{op.operador}</span>
                                    </div>
                                    <span className="badge bg-danger">{op.comAtraso}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                          <h5 className="text-success">Excelente!</h5>
                          <p>Nenhum cliente inadimplente no momento.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico de barras comparativo */}
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-primary"></i>
                        Comparativo de Entregas por Operador
                      </h5>
                    </div>
                    <div className="card-body">
                      {estatisticasPorOperador.length > 0 ? (
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={estatisticasPorOperador} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="operador" 
                              angle={-45} 
                              textAnchor="end" 
                              height={80}
                              interval={0}
                            />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="completas" name="Completas" fill={CORES.completa} stackId="a" />
                            <Bar dataKey="parciais" name="Parciais" fill={CORES.parcial} stackId="a" />
                            <Bar dataKey="atrasadas" name="Atrasadas" fill={CORES.atrasada} stackId="a" />
                            <Bar dataKey="atrasadasParciais" name="Atras. Parciais" fill={CORES.atrasadaParcial} stackId="a" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabela detalhada por operador */}
              <div className="card shadow-sm">
                <div className="card-header bg-white">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-table me-2 text-primary"></i>
                    Detalhamento por Operador
                  </h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover table-striped align-middle">
                      <thead className="table-dark">
                        <tr>
                          <th>Operador</th>
                          <th className="text-center">Total</th>
                          <th className="text-center">Completas</th>
                          <th className="text-center">Parciais</th>
                          <th className="text-center">Atrasadas</th>
                          <th className="text-center">Taxa Em Dia</th>
                          <th className="text-center">Média Atraso</th>
                          <th className="text-center">Pendências</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estatisticasPorOperador.map((op, index) => (
                          <tr key={op.operador}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div 
                                  className="operador-avatar me-2"
                                  style={{ backgroundColor: CORES_OPERADORES[index % CORES_OPERADORES.length] }}
                                >
                                  {op.operador.charAt(0).toUpperCase()}
                                </div>
                                <span className="fw-semibold">{op.operador}</span>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-secondary fs-6">{op.total}</span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-success">{op.completas}</span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-warning text-dark">{op.parciais}</span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-danger">{op.atrasadas + op.atrasadasParciais}</span>
                            </td>
                            <td className="text-center">
                              <div className="progress" style={{ height: '20px', minWidth: '80px' }}>
                                <div 
                                  className={`progress-bar ${parseFloat(op.taxaEmDia) >= 80 ? 'bg-success' : parseFloat(op.taxaEmDia) >= 60 ? 'bg-warning' : 'bg-danger'}`}
                                  style={{ width: `${op.taxaEmDia}%` }}
                                >
                                  {op.taxaEmDia}%
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className={`badge ${parseFloat(op.mediaDiasAtraso) > 10 ? 'bg-danger' : 'bg-info'}`}>
                                {op.mediaDiasAtraso} dias
                              </span>
                            </td>
                            <td className="text-center">
                              {op.pendenciasNaoResolvidas > 0 ? (
                                <span className="badge bg-danger">{op.pendenciasNaoResolvidas}</span>
                              ) : (
                                <span className="badge bg-success">0</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aba Pendências */}
          {abaAtiva === 'pendencias' && (
            <div className="tab-pane-custom">
              {/* Cards de resumo de pendências */}
              <div className="row g-4 mb-4">
                <div className="col-md-4">
                  <div className="kpi-card kpi-warning">
                    <div className="kpi-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{pendenciasParciais.length}</h3>
                      <p className="kpi-label">Pendências Abertas</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="kpi-card kpi-success">
                    <div className="kpi-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {pendenciasResolvidasPorOperador.reduce((acc, op) => acc + op.resolvidas, 0)}
                      </h3>
                      <p className="kpi-label">Pendências Resolvidas</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="kpi-card kpi-danger">
                    <div className="kpi-icon">
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {pendenciasParciais.length > 0 
                          ? Math.round(pendenciasParciais.reduce((acc, p) => acc + p.diasPendente, 0) / pendenciasParciais.length)
                          : 0
                        }
                      </h3>
                      <p className="kpi-label">Média Dias Pendente</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabela de Pendências Resolvidas por Operador */}
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-white">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-user-check me-2 text-success"></i>
                    Pendências Resolvidas por Operador
                  </h5>
                </div>
                <div className="card-body">
                  {pendenciasResolvidasPorOperador.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead className="table-success">
                          <tr>
                            <th>Operador</th>
                            <th className="text-center">Total Pendências</th>
                            <th className="text-center">Resolvidas</th>
                            <th className="text-center">Não Resolvidas</th>
                            <th className="text-center">Taxa de Resolução</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendenciasResolvidasPorOperador.map((op, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div 
                                    className="operador-avatar me-2"
                                    style={{ backgroundColor: CORES_OPERADORES[index % CORES_OPERADORES.length] }}
                                  >
                                    {op.operador.charAt(0).toUpperCase()}
                                  </div>
                                  <span className="fw-semibold">{op.operador}</span>
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-secondary fs-6">{op.total}</span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-success fs-6">{op.resolvidas}</span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-danger fs-6">{op.naoResolvidas}</span>
                              </td>
                              <td className="text-center">
                                <div className="progress" style={{ height: '22px', minWidth: '100px' }}>
                                  <div 
                                    className={`progress-bar ${
                                      parseFloat(op.taxaResolucao) >= 80 ? 'bg-success' : 
                                      parseFloat(op.taxaResolucao) >= 50 ? 'bg-warning' : 'bg-danger'
                                    }`}
                                    style={{ width: `${op.taxaResolucao}%` }}
                                  >
                                    {op.taxaResolucao}%
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <i className="fas fa-inbox fa-2x mb-2"></i>
<p>Nenhuma pendência registrada no período</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tabela de Pendências Não Resolvidas */}
              <div className="card shadow-sm">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                    Pendências Não Resolvidas
                  </h5>
                  <span className="badge bg-danger fs-6">
                    {pendenciasParciais.length} pendência(s)
                  </span>
                </div>
                <div className="card-body">
                  {pendenciasParciais.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead className="table-warning">
                          <tr>
                            <th>Data Criação</th>
                            <th className="text-center">Dias Pendente</th>
                            <th>Empresa</th>
                            <th>Operador</th>
                            <th>Tipo</th>
                            <th>Status</th>
                            <th>Observação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendenciasParciais.map((item, index) => (
                            <tr key={index} className={item.diasPendente > 30 ? 'table-danger' : item.diasPendente > 15 ? 'table-warning' : ''}>
                              <td>
                                <small className="text-muted">
                                  <i className="fas fa-calendar me-1"></i>
                                  {item.data_hoje?.split(' ')[0] || '-'}
                                </small>
                              </td>
                              <td className="text-center">
                                <span className={`badge fs-6 ${
                                  item.diasPendente > 30 ? 'bg-danger' :
                                  item.diasPendente > 15 ? 'bg-warning text-dark' :
                                  item.diasPendente > 7 ? 'bg-info' : 'bg-success'
                                }`}>
                                  {item.diasPendente} dias
                                </span>
                              </td>
                              <td className="fw-semibold">{item.empresa || '-'}</td>
                              <td>
                                <span className="badge bg-info">{item.operador || '-'}</span>
                              </td>
                              <td>
                                <span className={`badge ${
                                  item.tipo_entrega === 'Parcial' ? 'bg-warning text-dark' :
                                  item.tipo_entrega === 'Atrasada e Parcial' ? 'bg-danger' : 'bg-secondary'
                                }`}>
                                  {item.tipo_entrega || '-'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${
                                  item.status_entrega?.toLowerCase().includes('atrasado') 
                                    ? 'bg-danger' 
                                    : 'bg-success'
                                }`}>
                                  {item.status_entrega || '-'}
                                </span>
                              </td>
                              <td>
                                {item.texto_livre ? (
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setModalObservacao({
                                      show: true,
                                      texto: item.texto_livre,
                                      empresa: item.empresa
                                    })}
                                    title="Clique para ver completo"
                                  >
                                    <i className="fas fa-eye me-1"></i>
                                    Ver observação
                                  </button>
                                ) : (
                                  <small className="text-muted">
                                    <i className="fas fa-minus me-1"></i>
                                    Sem observação
                                  </small>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-check-circle fa-4x text-success mb-3"></i>
                      <h4 className="text-success">Nenhuma pendência!</h4>
                      <p className="text-muted">Todas as entregas parciais foram resolvidas.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Aba Evolução */}
          {abaAtiva === 'evolucao' && (
            <div className="tab-pane-custom">
              <div className="row g-4">
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-line me-2 text-primary"></i>
                        Evolução Mensal de Entregas
                      </h5>
                    </div>
                    <div className="card-body">
                      {evolucaoMensal.length > 0 ? (
                        <ResponsiveContainer width="100%" height={400}>
                          <AreaChart data={evolucaoMensal} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="mes" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Area 
                              type="monotone" 
                              dataKey="total" 
                              name="Total" 
                              stroke="#3498db" 
                              fill="#3498db" 
                              fillOpacity={0.3} 
                            />
                            <Area 
                              type="monotone" 
                              dataKey="completas" 
                              name="Completas" 
                              stroke={CORES.completa} 
                              fill={CORES.completa} 
                              fillOpacity={0.3} 
                            />
                            <Area 
                              type="monotone" 
                              dataKey="emDia" 
                              name="Em Dia" 
                              stroke={CORES.emDia} 
                              fill={CORES.emDia} 
                              fillOpacity={0.3} 
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível para o período selecionado</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-primary"></i>
                        Comparativo Mensal por Tipo
                      </h5>
                    </div>
                    <div className="card-body">
                      {evolucaoMensal.length > 0 ? (
                        <ResponsiveContainer width="100%" height={350}>
                          <BarChart data={evolucaoMensal} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="mes" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="completas" name="Completas" fill={CORES.completa} />
                            <Bar dataKey="parciais" name="Parciais" fill={CORES.parcial} />
                            <Bar dataKey="atrasadas" name="Atrasadas" fill={CORES.atrasada} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível para o período selecionado</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aba Inadimplência */}
          {abaAtiva === 'inadimplencia' && (
            <div className="tab-pane-custom">
              {/* Cards de resumo */}
              <div className="row g-4 mb-4">
                <div className="col-md-4">
                  <div className="kpi-card kpi-danger">
                    <div className="kpi-icon">
                      <i className="fas fa-exclamation-circle"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{empresasInadimplentes.length}</h3>
                      <p className="kpi-label">Total Inadimplentes</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="kpi-card kpi-warning">
                    <div className="kpi-icon">
                      <i className="fas fa-comment-slash"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {empresasInadimplentes.filter(emp => !emp.observacao || emp.observacao.trim() === '').length}
                      </h3>
                      <p className="kpi-label">Sem Justificativa</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="kpi-card kpi-success">
                    <div className="kpi-icon">
                      <i className="fas fa-comment-dots"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {empresasInadimplentes.filter(emp => emp.observacao && emp.observacao.trim() !== '').length}
                      </h3>
                      <p className="kpi-label">Com Justificativa</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros */}
              <div className="card shadow-sm mb-4">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-search me-2 text-primary"></i>
                        Buscar Empresa ou Operador
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Digite o nome da empresa ou operador..."
                        value={filtroInadimplencia}
                        onChange={(e) => setFiltroInadimplencia(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-filter me-2 text-primary"></i>
                        Status de Observação
                      </label>
                      <select
                        className="form-select"
                        value={filtroObservacaoInadimplencia}
                        onChange={(e) => setFiltroObservacaoInadimplencia(e.target.value)}
                      >
                        <option value="">Todos</option>
                        <option value="sem_obs">Sem Justificativa</option>
                        <option value="com_obs">Com Justificativa</option>
                      </select>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        className="btn btn-secondary w-100"
                        onClick={() => {
                          setFiltroInadimplencia('');
                          setFiltroObservacaoInadimplencia('');
                        }}
                      >
                        <i className="fas fa-eraser me-2"></i>
                        Limpar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico de Pizza - Distribuição */}
              <div className="row g-4 mb-4">
                <div className="col-lg-6">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-pie me-2 text-danger"></i>
                        Distribuição por Status de Observação
                      </h5>
                    </div>
                    <div className="card-body">
                      {empresasInadimplentes.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                {
                                  name: 'Sem Justificativa',
                                  value: empresasInadimplentes.filter(emp => !emp.observacao || emp.observacao.trim() === '').length,
                                  color: '#ffc107'
                                },
                                {
                                  name: 'Com Justificativa',
                                  value: empresasInadimplentes.filter(emp => emp.observacao && emp.observacao.trim() !== '').length,
                                  color: '#28a745'
                                }
                              ]}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              innerRadius={60}
                              paddingAngle={3}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {[
                                { color: '#ffc107' },
                                { color: '#28a745' }
                              ].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Empresas']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                          <p>Nenhuma empresa inadimplente!</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-danger"></i>
                        Top 5 Operadores com Mais Inadimplentes
                      </h5>
                    </div>
                    <div className="card-body">
                      {empresasInadimplentes.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={(() => {
                              const operadoresCount = {};
                              empresasInadimplentes.forEach(emp => {
                                const op = emp.operador || 'Não identificado';
                                operadoresCount[op] = (operadoresCount[op] || 0) + 1;
                              });
                              return Object.entries(operadoresCount)
                                .map(([operador, total]) => ({ operador, total }))
                                .sort((a, b) => b.total - a.total)
                                .slice(0, 5);
                            })()}
                            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="operador" 
                              angle={-45} 
                              textAnchor="end" 
                              height={80}
                              interval={0}
                            />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="total" name="Inadimplentes" fill="#dc3545" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-inbox fa-3x mb-3"></i>
                          <p>Nenhum dado disponível</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Top 3 Principais Impedimentos */}
              {empresasInadimplentes.filter(emp => emp.observacao && emp.observacao.trim() !== '').length > 0 && (
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-lightbulb me-2 text-warning"></i>
                      Principais Impedimentos — Por que estão em atraso?
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      {(() => {
                        const categorias = [
                          {
                            nome: 'Sem retorno do cliente',
                            icone: 'fas fa-phone-slash',
                            cor: 'danger',
                            palavras: ['sem retorno', 'nao retornou', 'nao responde', 'retorno cliente', 'nao atende', 'cliente nao', 'nao conseguimos contato', 'nao consegui contato', 'sem contato', 'nao foi possivel contato', 'aguardando retorno']
                          },
                          {
                            nome: 'Documentação não recebida',
                            icone: 'fas fa-file-alt',
                            cor: 'warning',
                            palavras: ['documentac', 'documento', 'nao recebemos', 'nao enviou', 'nao mandou', 'nada de doc', 'sem doc', 'nao nos enviou', 'nao nos mandou', 'pendente de doc', 'falta doc', 'aguardando doc', 'nao recebeu', 'nao recebemos nada']
                          },
                          {
                            nome: 'Cobrança enviada / Aguardando',
                            icone: 'fas fa-paper-plane',
                            cor: 'info',
                            palavras: ['cobran', 'enviei', 'enviamos', 'lembrete', 'follow', 'acompanhando', 'aguardando resposta', 'notificac', 'notificamos', 'mandei mensagem', 'mandamos mensagem']
                          },
                          {
                            nome: 'Férias / Afastamento',
                            icone: 'fas fa-umbrella-beach',
                            cor: 'secondary',
                            palavras: ['ferias', 'viagem', 'afastamento', 'licenca', 'ausente', 'recesso', 'folga']
                          },
                          {
                            nome: 'Problema / Pendência Interna',
                            icone: 'fas fa-exclamation-triangle',
                            cor: 'orange',
                            palavras: ['problema', 'pendencia', 'erro', 'sistema', 'interno', 'bloqueio', 'impedimento', 'dificuldade']
                          },
                          {
                            nome: 'Aguardando prazo / Combinado',
                            icone: 'fas fa-clock',
                            cor: 'primary',
                            palavras: ['aguardando', 'esperando', 'prazo', 'combinado', 'acordado', 'prometeu', 'prometeram', 'vai enviar', 'vai mandar']
                          },
                        ];

                        const normalize = str =>
                          str.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

                        const totalComObs = empresasInadimplentes.filter(
                          e => e.observacao && e.observacao.trim() !== ''
                        ).length;

                        const resultado = categorias.map(cat => {
                          const normPalavras = cat.palavras.map(normalize);
                          const matches = empresasInadimplentes.filter(emp => {
                            if (!emp.observacao || emp.observacao.trim() === '') return false;
                            const texto = normalize(emp.observacao);
                            return normPalavras.some(p => texto.includes(p));
                          });
                          return {
                            ...cat,
                            count: matches.length,
                            exemplos: matches.slice(0, 2).map(e => e.observacao)
                          };
                        })
                          .filter(c => c.count > 0)
                          .sort((a, b) => b.count - a.count)
                          .slice(0, 3);

                        const coresBorda = ['danger', 'warning', 'info'];

                        if (resultado.length === 0) {
                          return (
                            <div className="col-12 text-center text-muted py-3">
                              <i className="fas fa-search me-2"></i>
                              Não foi possível categorizar as justificativas automaticamente.
                            </div>
                          );
                        }

                        return resultado.map((cat, index) => {
                          const pct = totalComObs > 0
                            ? ((cat.count / totalComObs) * 100).toFixed(0)
                            : 0;
                          const corBorda = coresBorda[index] || cat.cor;
                          return (
                            <div key={index} className="col-md-4">
                              <div className={`border-start border-4 border-${corBorda} rounded p-3 bg-light h-100`}>
                                <div className="d-flex align-items-center mb-2">
                                  <i className={`${cat.icone} text-${corBorda} me-2 fa-lg`}></i>
                                  <strong className="small flex-grow-1">{cat.nome}</strong>
                                  <span className={`badge bg-${corBorda}`}>
                                    {cat.count} empresa{cat.count !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                <div className="progress mb-1" style={{ height: '6px' }}>
                                  <div
                                    className={`progress-bar bg-${corBorda}`}
                                    style={{ width: `${pct}%` }}
                                  ></div>
                                </div>
                                <small className="text-muted d-block mb-2">
                                  {pct}% das empresas com justificativa
                                </small>
                                {cat.exemplos.length > 0 && (
                                  <div className="border-top pt-2 mt-1">
                                    <small className="text-muted fw-semibold d-block mb-1">Exemplos:</small>
                                    {cat.exemplos.map((ex, i) => (
                                      <div key={i} className="small text-muted fst-italic mb-1">
                                        "{ex.length > 90 ? ex.substring(0, 90) + '…' : ex}"
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Tabela de empresas inadimplentes */}
              <div className="card shadow-sm">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-list me-2 text-danger"></i>
                    Empresas Inadimplentes (+10 dias)
                  </h5>
                  <span className="badge bg-danger fs-6">
                    {(() => {
                      let filtradas = empresasInadimplentes;
                      
                      if (filtroInadimplencia) {
                        filtradas = filtradas.filter(emp =>
                          emp.empresa?.toLowerCase().includes(filtroInadimplencia.toLowerCase()) ||
                          emp.operador?.toLowerCase().includes(filtroInadimplencia.toLowerCase())
                        );
                      }
                      
                      if (filtroObservacaoInadimplencia === 'sem_obs') {
                        filtradas = filtradas.filter(emp => !emp.observacao || emp.observacao.trim() === '');
                      } else if (filtroObservacaoInadimplencia === 'com_obs') {
                        filtradas = filtradas.filter(emp => emp.observacao && emp.observacao.trim() !== '');
                      }
                      
                      return filtradas.length;
                    })()} empresa(s)
                  </span>
                </div>
                <div className="card-body">
                  {carregandoInadimplencia ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-danger mb-3" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="text-muted">Carregando empresas inadimplentes...</p>
                    </div>
                  ) : empresasInadimplentes.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead className="table-danger">
                          <tr>
                            <th><i className="fas fa-barcode me-2"></i>Nº Domínio</th>
                            <th><i className="fas fa-building me-2"></i>Empresa</th>
                            <th><i className="fas fa-user me-2"></i>Operador</th>
                            <th><i className="fas fa-book me-2"></i>Regime</th>
                            <th><i className="fas fa-calendar-check me-2"></i>Competência</th>
                            <th><i className="fas fa-calendar-times me-2"></i>Próxima Entrega</th>
                            <th className="text-center"><i className="fas fa-clock me-2"></i>Dias Atraso</th>
                            <th className="text-center"><i className="fas fa-comment me-2"></i>Observação</th>
                            <th className="text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            let filtradas = empresasInadimplentes;
                            
                            if (filtroInadimplencia) {
                              filtradas = filtradas.filter(emp =>
                                emp.empresa?.toLowerCase().includes(filtroInadimplencia.toLowerCase()) ||
                                emp.operador?.toLowerCase().includes(filtroInadimplencia.toLowerCase())
                              );
                            }
                            
                            if (filtroObservacaoInadimplencia === 'sem_obs') {
                              filtradas = filtradas.filter(emp => !emp.observacao || emp.observacao.trim() === '');
                            } else if (filtroObservacaoInadimplencia === 'com_obs') {
                              filtradas = filtradas.filter(emp => emp.observacao && emp.observacao.trim() !== '');
                            }
                            
                            filtradas.sort((a, b) => b.dias_atraso - a.dias_atraso);
                            
                            return filtradas;
                          })().map((empresa, index) => (
                            <tr 
                              key={`${empresa.numero_dominio}-${index}`}
                              className={empresa.dias_atraso > 30 ? 'table-danger' : empresa.dias_atraso > 20 ? 'table-warning' : ''}
                            >
                              <td><code>{empresa.numero_dominio}</code></td>
                              <td className="fw-semibold">{empresa.empresa}</td>
                              <td>
                                <span className="badge bg-info">{empresa.operador || 'N/A'}</span>
                              </td>
                              <td>
                                <span className="badge bg-secondary">{empresa.regime || '-'}</span>
                              </td>
                              <td>
                                <span className="badge bg-primary">{empresa.competencia || '-'}</span>
                              </td>
                              <td>{empresa.proxima_entrega || '-'}</td>
                              <td className="text-center">
                                <span className={`badge fs-6 ${
                                  empresa.dias_atraso > 30 ? 'bg-danger' :
                                  empresa.dias_atraso > 20 ? 'bg-warning text-dark' :
                                  'bg-danger'
                                }`}>
                                  <i className="fas fa-clock me-1"></i>
                                  {empresa.dias_atraso} dias
                                </span>
                              </td>
                              <td className="text-center">
                                {empresa.observacao && empresa.observacao.trim() !== '' ? (
                                  <div>
                                    <span className="badge bg-success mb-1">
                                      <i className="fas fa-check-circle me-1"></i>
                                      Com justificativa
                                    </span>
                                    <br />
                                    <button
                                      className="btn btn-sm btn-outline-success mt-1"
                                      onClick={() => setModalObservacao({
                                        show: true,
                                        texto: empresa.observacao,
                                        empresa: empresa.empresa
                                      })}
                                    >
                                      <i className="fas fa-eye me-1"></i>
                                      Ver
                                    </button>
                                    {empresa.data_observacao && (
                                      <small className="d-block text-muted mt-1">
                                        {empresa.data_observacao}
                                      </small>
                                    )}
                                  </div>
                                ) : (
                                  <span className="badge bg-warning text-dark">
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    Sem justificativa
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => window.location.href = '/Atrasoatual'}
                                  title="Gerenciar Atrasos"
                                >
                                  <i className="fas fa-edit me-1"></i>
                                  Gerenciar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {(() => {
                        let filtradas = empresasInadimplentes;
                        
                        if (filtroInadimplencia) {
                          filtradas = filtradas.filter(emp =>
                            emp.empresa?.toLowerCase().includes(filtroInadimplencia.toLowerCase()) ||
                            emp.operador?.toLowerCase().includes(filtroInadimplencia.toLowerCase())
                          );
                        }
                        
                        if (filtroObservacaoInadimplencia === 'sem_obs') {
                          filtradas = filtradas.filter(emp => !emp.observacao || emp.observacao.trim() === '');
                        } else if (filtroObservacaoInadimplencia === 'com_obs') {
                          filtradas = filtradas.filter(emp => emp.observacao && emp.observacao.trim() !== '');
                        }
                        
                        return filtradas.length === 0 && (filtroInadimplencia || filtroObservacaoInadimplencia);
                      })() && (
                        <div className="text-center text-muted py-4">
                          <i className="fas fa-search fa-2x mb-2"></i>
                          <p>Nenhuma empresa encontrada com os filtros aplicados</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-check-circle fa-4x text-success mb-3"></i>
                      <h4 className="text-success">Excelente!</h4>
<p className="text-muted">Nenhuma empresa com mais de 10 dias de atraso no momento.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Aba Histórico de Atrasos */}
          {abaAtiva === 'historico-atrasos' && (
            <div className="tab-pane-custom">
              {/* Cards de resumo */}
              <div className="row g-4 mb-4">
                <div className="col-md-4">
                  <div className="kpi-card kpi-danger">
                    <div className="kpi-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{historicoAtrasos.length}</h3>
                      <p className="kpi-label">Total de Atrasos Registrados</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="kpi-card kpi-warning">
                    <div className="kpi-icon">
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {historicoAtrasos.filter(h => h.tipo_entrega === 'Atrasada').length}
                      </h3>
                      <p className="kpi-label">Entregas Atrasadas Completas</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="kpi-card kpi-warning">
                    <div className="kpi-icon">
                      <i className="fas fa-hourglass-half"></i>
                    </div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {historicoAtrasos.filter(h => h.tipo_entrega === 'Atrasada e Parcial').length}
                      </h3>
                      <p className="kpi-label">Entregas Atrasadas Parciais</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico - Atrasos por Operador */}
              <div className="row g-4 mb-4">
                <div className="col-lg-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-danger"></i>
                        Atrasos por Operador
                      </h5>
                    </div>
                    <div className="card-body">
                      {Object.keys(estatisticasOperadoresAtrasos).length > 0 ? (
                        <ResponsiveContainer width="100%" height={350}>
                          <BarChart
                            data={Object.entries(estatisticasOperadoresAtrasos).map(([operador, stats]) => ({
                              operador,
                              atrasadas: stats.atrasadas,
                              atrasadas_parciais: stats.atrasadas_parciais,
                              total: stats.total_atrasos
                            })).sort((a, b) => b.total - a.total)}
                            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="operador" 
                              angle={-45} 
                              textAnchor="end" 
                              height={100}
                              interval={0}
                            />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="atrasadas" name="Atrasadas Completas" fill="#dc3545" stackId="a" />
                            <Bar dataKey="atrasadas_parciais" name="Atrasadas Parciais" fill="#ffc107" stackId="a" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted py-5">
                          <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                          <p>Nenhum atraso registrado!</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros */}
              <div className="card shadow-sm mb-4">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-search me-2 text-primary"></i>
                        Buscar por Empresa ou Competência
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Digite o nome da empresa ou competência..."
                        value={filtroAtrasos}
                        onChange={(e) => setFiltroAtrasos(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-user me-2 text-primary"></i>
                        Filtrar por Operador
                      </label>
                      <select
                        className="form-select"
                        value={filtroOperadorAtrasos}
                        onChange={(e) => setFiltroOperadorAtrasos(e.target.value)}
                      >
                        <option value="">Todos os Operadores</option>
                        {Object.keys(estatisticasOperadoresAtrasos).map((operador) => (
                          <option key={operador} value={operador}>{operador}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        className="btn btn-secondary w-100"
                        onClick={() => {
                          setFiltroAtrasos('');
                          setFiltroOperadorAtrasos('');
                        }}
                      >
                        <i className="fas fa-eraser me-2"></i>
                        Limpar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabela de histórico de atrasos */}
              <div className="card shadow-sm">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-list me-2 text-danger"></i>
                    Histórico Completo de Entregas Atrasadas
                  </h5>
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={carregarHistoricoAtrasos}
                    disabled={carregandoAtrasos}
                  >
                    <i className={`fas fa-sync-alt me-2 ${carregandoAtrasos ? 'fa-spin' : ''}`}></i>
                    Atualizar
                  </button>
                </div>
                <div className="card-body">
                  {carregandoAtrasos ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-danger mb-3" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="text-muted">Carregando histórico de atrasos...</p>
                    </div>
                  ) : historicoAtrasos.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle">
                        <thead className="table-danger">
                          <tr>
                            <th><i className="fas fa-calendar me-2"></i>Data/Hora</th>
                            <th><i className="fas fa-building me-2"></i>Empresa</th>
                            <th><i className="fas fa-barcode me-2"></i>Nº Domínio</th>
                            <th><i className="fas fa-calendar-check me-2"></i>Competência</th>
                            <th><i className="fas fa-user me-2"></i>Operador</th>
                            <th><i className="fas fa-tag me-2"></i>Tipo</th>
                            <th><i className="fas fa-info-circle me-2"></i>Status</th>
                            <th><i className="fas fa-comment me-2"></i>Motivo do Atraso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            let filtrados = historicoAtrasos;
                            
                            if (filtroAtrasos) {
                              filtrados = filtrados.filter(item =>
                                item.empresa?.toLowerCase().includes(filtroAtrasos.toLowerCase()) ||
                                item.entregue?.toLowerCase().includes(filtroAtrasos.toLowerCase())
                              );
                            }
                            
                            if (filtroOperadorAtrasos) {
                              filtrados = filtrados.filter(item => item.operador === filtroOperadorAtrasos);
                            }
                            
                            return filtrados.length > 0 ? (
                              filtrados.map((item, index) => (
                                <tr key={item.id || index}>
                                  <td>{item.data_hoje}</td>
                                  <td className="fw-semibold">{item.empresa}</td>
                                  <td><code>{item.numero_dominio}</code></td>
                                  <td>
                                    <span className="badge bg-primary">{item.entregue}</span>
                                  </td>
                                  <td>
                                    <span className="badge bg-info">{item.operador || 'N/A'}</span>
                                  </td>
                                  <td>
                                    <span className={`badge ${
                                      item.tipo_entrega === 'Atrasada' 
                                        ? 'bg-danger' 
                                        : 'bg-warning text-dark'
                                    }`}>
                                      {item.tipo_entrega}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge ${
                                      item.status_entrega?.includes('Atrasado') 
                                        ? 'bg-danger' 
                                        : 'bg-secondary'
                                    }`}>
                                      {item.status_entrega || 'Não informado'}
                                    </span>
                                  </td>
                                  <td style={{ maxWidth: '300px' }}>
                                    {item.texto_livre ? (
                                      <div className="text-danger" style={{ 
                                        whiteSpace: 'pre-wrap', 
                                        wordBreak: 'break-word',
                                        fontSize: '0.9rem'
                                      }}>
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        <span className="fst-italic">{item.texto_livre}</span>
                                      </div>
                                    ) : (
                                      <span className="text-muted">
                                        <i className="fas fa-minus me-2"></i>
                                        Sem motivo registrado
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center text-muted py-4">
                                  <i className="fas fa-search fa-2x mb-2"></i>
                                  <p>Nenhum atraso encontrado com os filtros aplicados</p>
                                </td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-check-circle fa-4x text-success mb-3"></i>
                      <h4 className="text-success">Excelente!</h4>
                      <p className="text-muted">Nenhuma entrega atrasada registrada no histórico.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Aba Envio de Fechamento */}
          {abaAtiva === 'envio-fechamento' && (
            <div className="tab-pane-custom">

              {/* KPIs */}
              <div className="row g-4 mb-4">
                <div className="col-md-3">
                  <div className="kpi-card kpi-total">
                    <div className="kpi-icon"><i className="fas fa-paper-plane"></i></div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">{historicoFechamentos.length}</h3>
                      <p className="kpi-label">Total Enviados</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="kpi-card kpi-completa">
                    <div className="kpi-icon"><i className="fas fa-check-circle"></i></div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {historicoFechamentos.filter(h => h.status_envio === 'ENVIADO').length}
                      </h3>
                      <p className="kpi-label">Enviados com Sucesso</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="kpi-card kpi-danger">
                    <div className="kpi-icon"><i className="fas fa-times-circle"></i></div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {historicoFechamentos.filter(h => h.status_envio === 'ERRO').length}
                      </h3>
                      <p className="kpi-label">Erros de Envio</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="kpi-card kpi-parcial">
                    <div className="kpi-icon"><i className="fas fa-users"></i></div>
                    <div className="kpi-content">
                      <h3 className="kpi-value">
                        {[...new Set(historicoFechamentos.map(h => h.operador).filter(Boolean))].length}
                      </h3>
                      <p className="kpi-label">Operadores Ativos</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabela */}
              <div className="card shadow-sm">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-list me-2 text-primary"></i>
                    Histórico de Envios de Fechamento Contábil
                  </h5>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={carregarHistoricoFechamentos}
                    disabled={carregandoFechamentos}
                  >
                    <i className={`fas fa-sync-alt me-2 ${carregandoFechamentos ? 'fa-spin' : ''}`}></i>
                    Atualizar
                  </button>
                </div>
                <div className="card-body">
                  {carregandoFechamentos ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="text-muted">Carregando histórico de envios...</p>
                    </div>
                  ) : (() => {
                    let filtrados = historicoFechamentos;

                    if (filtroOperador)
                      filtrados = filtrados.filter(h => h.operador === filtroOperador);

                    if (filtroPeriodo && filtroPeriodo !== 'todos') {
                      const diasFiltro = parseInt(filtroPeriodo);
                      const dataLimite = new Date();
                      dataLimite.setDate(dataLimite.getDate() - diasFiltro);
                      filtrados = filtrados.filter(h => {
                        if (!h.data_envio) return false;
                        const partes = h.data_envio.split(' ')[0].split('/');
                        if (partes.length !== 3) return false;
                        const dataItem = new Date(parseInt(partes[2]), parseInt(partes[1]) - 1, parseInt(partes[0]));
                        return dataItem >= dataLimite;
                      });
                    }

                    return filtrados.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover align-middle" style={{ fontSize: '0.85rem' }}>
                          <thead className="table-primary">
                            <tr>
                              <th className="text-nowrap"><i className="fas fa-calendar me-1"></i>Data/Hora</th>
                              <th><i className="fas fa-building me-1"></i>Empresa</th>
                              <th><i className="fas fa-barcode me-1"></i>Nº Dom.</th>
                              <th className="text-nowrap"><i className="fas fa-calendar-check me-1"></i>Período</th>
                              <th><i className="fas fa-user me-1"></i>Operador</th>
                              <th className="text-nowrap text-end">Rec. Op. Bruta</th>
                              <th className="text-nowrap text-end">Deduções</th>
                              <th className="text-nowrap text-end">Rec. Líquida</th>
                              <th className="text-nowrap text-end">Lucro Bruto</th>
                              <th className="text-nowrap text-end">Desp. Operac.</th>
                              <th className="text-nowrap text-end">Res. Financeiro</th>
                              <th className="text-nowrap text-end">Res. Antes IR</th>
                              <th className="text-nowrap text-end">Lucro Líquido</th>
                              <th><i className="fas fa-comment me-1"></i>Msg. Assessor</th>
                              <th><i className="fas fa-tag me-1"></i>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtrados.map((item, index) => {
                              // dados_dre pode vir como dicionário (formato antigo) ou como lista
                              // [{chave, valor}] (formato gravado desde a edição no duplo check);
                              // normaliza para dicionário para as colunas não saírem vazias
                              const dreBruto = item.dados_dre || {};
                              const dre = Array.isArray(dreBruto)
                                ? Object.fromEntries(dreBruto.map(l => [l.chave, l.valor]))
                                : dreBruto;
                              const fmt = (v) => v != null
                                ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : '—';
                              const cor = (v) => v == null ? '' : v >= 0 ? 'text-success' : 'text-danger';
                              return (
                                <tr key={item.id || index}>
                                  <td className="text-nowrap">{item.data_envio || '—'}</td>
                                  <td className="fw-semibold" style={{ minWidth: '160px' }}>{item.empresa}</td>
                                  <td><code>{item.numero_dominio}</code></td>
                                  <td><span className="badge bg-primary">{item.mes_ano}</span></td>
                                  <td><span className="badge bg-info">{item.operador || 'N/A'}</span></td>
                                  <td className={`text-end text-nowrap ${cor(dre['RECEITA OPERACIONAL BRUTA'])}`}>
                                    {fmt(dre['RECEITA OPERACIONAL BRUTA'])}
                                  </td>
                                  <td className={`text-end text-nowrap ${cor(dre['DEDUÇÕES DA RECEITA BRUTA'])}`}>
                                    {fmt(dre['DEDUÇÕES DA RECEITA BRUTA'])}
                                  </td>
                                  <td className={`text-end text-nowrap ${cor(dre['RECEITA LÍQUIDA OPERACIONAL'])}`}>
                                    {fmt(dre['RECEITA LÍQUIDA OPERACIONAL'])}
                                  </td>
                                  <td className={`text-end text-nowrap fw-semibold ${cor(dre['LUCRO BRUTO'])}`}>
                                    {fmt(dre['LUCRO BRUTO'])}
                                  </td>
                                  <td className={`text-end text-nowrap ${cor(dre['DESPESAS OPERACIONAIS'])}`}>
                                    {fmt(dre['DESPESAS OPERACIONAIS'])}
                                  </td>
                                  <td className={`text-end text-nowrap ${cor(dre['RESULTADO FINANCEIRO LÍQUIDO'])}`}>
                                    {fmt(dre['RESULTADO FINANCEIRO LÍQUIDO'])}
                                  </td>
                                  <td className={`text-end text-nowrap ${cor(dre['RESULTADO ANTES DO IRPJ E CSLL'])}`}>
                                    {fmt(dre['RESULTADO ANTES DO IRPJ E CSLL'])}
                                  </td>
                                  <td className={`text-end text-nowrap fw-bold ${cor(dre['LUCRO LÍQUIDO DO EXERCÍCIO'])}`}>
                                    {fmt(dre['LUCRO LÍQUIDO DO EXERCÍCIO'])}
                                  </td>
                                  <td>
                                    {item.mensagem_assessor ? (
                                      <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setModalObservacao({ show: true, texto: item.mensagem_assessor, empresa: item.empresa })}
                                      >
                                        <i className="fas fa-eye me-1"></i>Ver
                                      </button>
                                    ) : <span className="text-muted">—</span>}
                                  </td>
                                  <td>
                                    {item.status_envio === 'ENVIADO' ? (
                                      <span className="badge bg-success">
                                        <i className="fas fa-check me-1"></i>Enviado
                                      </span>
                                    ) : (
                                      <span className="badge bg-danger" title={item.mensagem_erro || ''}>
                                        <i className="fas fa-times me-1"></i>Erro
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <small className="text-muted">
                          Exibindo {filtrados.length} de {historicoFechamentos.length} registros
                        </small>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <i className="fas fa-search fa-3x text-muted mb-3"></i>
                        <p className="text-muted">Nenhum envio encontrado com os filtros aplicados.</p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* ===== ABA GESTÃO DE AUDITORIAS ===== */}
          {abaAtiva === 'auditoria' && (
            <div className="tab-pane-custom">
              {carregandoAuditorias ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="text-muted">Carregando dados de auditoria...</p>
                </div>
              ) : (() => {
                // Só apontamentos CONFIRMADOS pelo operador contam nesta aba — contestado
                // é discordância, não erro comprovado (mesma régua do boletim diário).
                // Auditoria sem nenhum confirmado não entra; "resolvida" = todos os
                // confirmados resolvidos.
                const audConf = auditorias
                  .map(a => {
                    const itens = (a.itens || []).filter(it => (it.conferencia || '').toLowerCase() === 'confirmado');
                    const sev = (v) => itens.filter(it => (it.severidade || '').toUpperCase() === v).length;
                    return {
                      ...a,
                      itens,
                      totais: { grave: sev('GRAVE'), medio: sev('MEDIO'), baixo: sev('BAIXO'),
                                manual: itens.filter(it => it.item_checklist === 'M').length },
                      resolvido: itens.length > 0 && itens.every(it => it.resolvido),
                      tem_contestado: false,
                    };
                  })
                  .filter(a => a.itens.length > 0)
                  // intervalo De/Até pela data da auditoria (data_iso = AAAA-MM-DDTHH:MM)
                  .filter(a => {
                    const dia = (a.data_iso || '').slice(0, 10);
                    if (audDe && dia && dia < audDe) return false;
                    if (audAte && dia && dia > audAte) return false;
                    return true;
                  });
                const totalAuditorias   = audConf.length;
                const empresasUnicas    = new Set(audConf.map(a => a.numero_dominio).filter(Boolean)).size;
                const totalGravesA      = audConf.reduce((s, a) => s + (a.totais?.grave  || 0), 0);
                const totalMediosA      = audConf.reduce((s, a) => s + (a.totais?.medio  || 0), 0);
                const totalBaixosA      = audConf.reduce((s, a) => s + (a.totais?.baixo  || 0), 0);
                const totalManuaisA     = audConf.reduce((s, a) => s + (a.totais?.manual || 0), 0);
                const totalAlertas      = totalGravesA + totalMediosA + totalBaixosA + totalManuaisA;
                const resolvidasCount   = audConf.filter(a => a.resolvido).length;
                const pendentesCount    = totalAuditorias - resolvidasCount;
                const taxaResolucao     = totalAuditorias > 0 ? Math.round((resolvidasCount / totalAuditorias) * 100) : 0;
                const mediAlertasPorAuditoria = totalAuditorias > 0 ? (totalAlertas / totalAuditorias).toFixed(1) : 0;

                // evolução mensal
                const evolMap = {};
                audConf.forEach(a => {
                  if (!a.data_auditoria) return;
                  const p = a.data_auditoria.split('/');
                  if (p.length < 3) return;
                  const key = `${p[1]}/${p[2]}`;
                  if (!evolMap[key]) evolMap[key] = { mes: key, total: 0, resolvidas: 0, graves: 0, alertas: 0 };
                  evolMap[key].total++;
                  if (a.resolvido) evolMap[key].resolvidas++;
                  evolMap[key].graves  += a.totais?.grave || 0;
                  evolMap[key].alertas += (a.totais?.grave || 0) + (a.totais?.medio || 0) + (a.totais?.baixo || 0);
                });
                const evolAuditoria = Object.values(evolMap).sort((a, b) => {
                  const [mA, yA] = a.mes.split('/').map(Number);
                  const [mB, yB] = b.mes.split('/').map(Number);
                  return yA !== yB ? yA - yB : mA - mB;
                });

                // top empresas com mais alertas
                const topEmpresas = [...audConf]
                  .map(a => ({
                    cliente: a.cliente || '—',
                    numero_dominio: a.numero_dominio,
                    periodo: a.periodo,
                    operador: a.operador,
                    graves: a.totais?.grave  || 0,
                    medios: a.totais?.medio  || 0,
                    baixos: a.totais?.baixo  || 0,
                    totalAlertas: (a.totais?.grave || 0) + (a.totais?.medio || 0) + (a.totais?.baixo || 0),
                    resolvido: a.resolvido,
                  }))
                  .filter(a => a.totalAlertas > 0)
                  .sort((a, b) => b.graves - a.graves || b.totalAlertas - a.totalAlertas)
                  .slice(0, 10);

                // ════════ Blocos novos (pedido do gestor) ════════
                // Regime em 3 grupos, cada um com a frequência de auditoria da regra
                const grupoRegime = (r) => {
                  const t = (r || '').toLowerCase();
                  if (t.includes('simples')) return 'SN';
                  if (t.includes('presumido') || t === 'lp') return 'LP';
                  if (t.includes('real') || t === 'lr' || t === 'lrt') return 'LR';
                  return 'OUTROS';
                };
                const REGIMES_AUD = {
                  SN: { nome: 'Simples Nacional', freq: 'Anual',      dias: 366, cor: '#0d6efd', bg: '#e7f0ff', icon: 'fa-leaf' },
                  LP: { nome: 'Lucro Presumido',  freq: 'Trimestral', dias: 92,  cor: '#d97706', bg: '#fffbeb', icon: 'fa-balance-scale' },
                  LR: { nome: 'Lucro Real',       freq: 'Mensal',     dias: 31,  cor: '#1a3a6b', bg: '#e8edf5', icon: 'fa-landmark' },
                };
                const paraData = (dmy) => {
                  const [d, m, y] = (dmy || '').split(' ')[0].split('/').map(Number);
                  return (d && m && y) ? new Date(y, m - 1, d) : null;
                };
                const hojeAud = new Date(); hojeAud.setHours(0, 0, 0, 0);
                const diasEntre = (a, b) => Math.round((b - a) / 86400000);

                // itens que entram nos rankings (por padrão só os confirmados)
                const itensDe = (a) => a.itens || [];   // audConf já traz só os confirmados
                const respDe = (a) => a.operador_empresa || a.operador || 'Não identificado';

                // (1) cobertura por tributação: carteira x auditadas. Conta TODA auditoria
                // feita, não só as com apontamento confirmado — auditoria limpa (sem erro)
                // também cobre a empresa — e ignora o filtro De/Até: "em dia" é medido
                // até hoje.
                const auditPorDominio = {};
                auditorias.forEach(a => {
                  const nd = String(a.numero_dominio || '').trim();
                  if (!nd) return;
                  const d = paraData(a.data_auditoria);
                  const reg = auditPorDominio[nd] || (auditPorDominio[nd] = { n: 0, ultima: null, periodos: new Set() });
                  reg.n++;
                  if (a.periodo) reg.periodos.add(a.periodo);
                  if (d && (!reg.ultima || d > reg.ultima)) reg.ultima = d;
                });
                const cobertura = { SN: [], LP: [], LR: [] };
                carteiraAud.forEach(c => {
                  const g = grupoRegime(c.regime);
                  if (!cobertura[g]) return;
                  const nd = String(c.numero_dominio || '').trim();
                  const au = auditPorDominio[nd];
                  const dias = au && au.ultima ? diasEntre(au.ultima, hojeAud) : null;
                  const situacao = !au ? 'nunca' : (dias != null && dias <= REGIMES_AUD[g].dias ? 'em_dia' : 'vencida');
                  cobertura[g].push({
                    empresa: c.empresa, numero_dominio: nd, operador: c.operador, regime: c.regime,
                    audConf: au ? au.n : 0,
                    competencias: au ? [...au.periodos].sort().join(', ') : '',
                    ultima: au && au.ultima ? au.ultima.toLocaleDateString('pt-BR') : '—',
                    dias, situacao,
                  });
                });
                Object.values(cobertura).forEach(l => l.sort((x, y) =>
                  ({ vencida: 0, nunca: 1, em_dia: 2 }[x.situacao] - { vencida: 0, nunca: 1, em_dia: 2 }[y.situacao])
                  || (x.empresa || '').localeCompare(y.empresa || '')));
                const totalCarteiraAud = carteiraAud.length;
                const empresasAuditadasCarteira = carteiraAud.filter(c => auditPorDominio[String(c.numero_dominio || '').trim()]).length;

                // (2) evolução com filtro de período e agrupamento por semana/mês
                const limiteEvol = evolPeriodo === 'todos' ? null
                  : new Date(hojeAud.getTime() - Number(evolPeriodo) * 86400000);
                const chaveSemana = (d) => {
                  const seg = new Date(d); const dow = (seg.getDay() + 6) % 7; // segunda = 0
                  seg.setDate(seg.getDate() - dow);
                  return seg;
                };
                const evolMap2 = {};
                audConf.forEach(a => {
                  const d = paraData(a.data_auditoria);
                  if (!d || (limiteEvol && d < limiteEvol)) return;
                  const base = evolAgrupar === 'semana' ? chaveSemana(d) : new Date(d.getFullYear(), d.getMonth(), 1);
                  const k = base.getTime();
                  const rot = evolAgrupar === 'semana'
                    ? `sem ${base.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`
                    : `${String(base.getMonth() + 1).padStart(2, '0')}/${base.getFullYear()}`;
                  const e = evolMap2[k] || (evolMap2[k] = { k, mes: rot, total: 0, resolvidas: 0, graves: 0 });
                  e.total++;
                  if (a.resolvido) e.resolvidas++;
                  e.graves += a.totais?.grave || 0;
                });
                const evolFiltrada = Object.values(evolMap2).sort((x, y) => x.k - y.k);

                // (3) ranking do time por regime: apontamentos x resolvidos x graves + tempo
                const nomesTime = new Set([
                  ...carteiraAud.map(c => c.operador).filter(Boolean),
                  ...audConf.map(respDe).filter(n => n && n !== 'Não identificado'),
                ]);
                const timeMap = {};
                nomesTime.forEach(n => { timeMap[n] = { operador: n, audConf: 0, total: 0, resolvidos: 0, graves: 0, medios: 0, diasRes: [] }; });
                audConf.forEach(a => {
                  if (rankRegime && grupoRegime(a.regime) !== rankRegime) return;
                  const r = timeMap[respDe(a)] || (timeMap[respDe(a)] = { operador: respDe(a), audConf: 0, total: 0, resolvidos: 0, graves: 0, medios: 0, diasRes: [] });
                  r.audConf++;
                  const dAud = paraData(a.data_auditoria);
                  itensDe(a).forEach(it => {
                    r.total++;
                    const sv = (it.severidade || '').toUpperCase();
                    if (sv === 'GRAVE') r.graves++;
                    if (sv === 'MEDIO') r.medios++;
                    if (it.resolvido) {
                      r.resolvidos++;
                      const dRes = paraData(it.resolvido_em);
                      if (dAud && dRes && dRes >= dAud) r.diasRes.push(diasEntre(dAud, dRes));
                    }
                  });
                });
                const rankingTime = Object.values(timeMap)
                  .map(r => ({
                    ...r,
                    pctResolvido: r.total ? Math.round((r.resolvidos / r.total) * 100) : 0,
                    tempoMedio: r.diasRes.length ? Math.round(r.diasRes.reduce((x, y) => x + y, 0) / r.diasRes.length) : null,
                  }))
                  .sort((x, y) => (rankOrdem === 'mais' ? (y.total - x.total) : (x.total - y.total))
                    || (rankOrdem === 'mais' ? y.graves - x.graves : x.graves - y.graves)
                    || x.operador.localeCompare(y.operador));

                // (4) menos alertas graves e médios — só quem teve auditoria no recorte
                const menosGraves = rankingTime
                  .filter(r => r.audConf > 0)
                  .map(r => ({ ...r, gm: r.graves + r.medios, gmPorAud: (r.graves + r.medios) / r.audConf }))
                  .sort((x, y) => x.gmPorAud - y.gmPorAud || x.gm - y.gm)
                  .slice(0, 5);

                // (5) erros mais frequentes: por categoria, com as descrições que mais aparecem
                const errosMap = {};
                audConf.forEach(a => {
                  if (rankRegime && grupoRegime(a.regime) !== rankRegime) return;
                  itensDe(a).forEach(it => {
                    const cat = (it.categoria || 'Sem categoria').trim();
                    const e = errosMap[cat] || (errosMap[cat] = { categoria: cat, total: 0, graves: 0, medios: 0, resolvidos: 0, empresas: new Set(), descricoes: {} });
                    e.total++;
                    const sv = (it.severidade || '').toUpperCase();
                    if (sv === 'GRAVE') e.graves++;
                    if (sv === 'MEDIO') e.medios++;
                    if (it.resolvido) e.resolvidos++;
                    if (a.numero_dominio) e.empresas.add(a.numero_dominio);
                    const desc = (it.descricao || '').trim();
                    if (desc) {
                      // agrupa descrições iguais ignorando o trecho entre aspas (nome da conta etc.)
                      const chave = desc.replace(/['"“”‘’][^'"“”‘’]*['"“”‘’]/g, '…').replace(/\s+/g, ' ');
                      e.descricoes[chave] = (e.descricoes[chave] || 0) + 1;
                    }
                  });
                });
                const rankingErros = Object.values(errosMap)
                  .map(e => ({
                    ...e,
                    nEmpresas: e.empresas.size,
                    topDesc: Object.entries(e.descricoes).sort((x, y) => y[1] - x[1]).slice(0, 2),
                  }))
                  .sort((x, y) => y.total - x.total || y.graves - x.graves)
                  .slice(0, 10);

                // Não resolvidos: item confirmado e ainda aberto, com dias desde a auditoria
                const pendentesAud = [];
                audConf.forEach(a => {
                  const d = paraData(a.data_auditoria);
                  a.itens.forEach(it => {
                    if (it.resolvido) return;
                    pendentesAud.push({
                      id: `${a.auditoria_id}-${it.id}`, cliente: a.cliente, numero_dominio: a.numero_dominio,
                      periodo: a.periodo, operador: respDe(a), severidade: (it.severidade || '').toUpperCase(),
                      categoria: it.categoria || '—', descricao: it.descricao || '', data: (a.data_auditoria || '').split(' ')[0],
                      dias: d ? diasEntre(d, hojeAud) : null, andamento: !!it.em_andamento,
                    });
                  });
                });
                pendentesAud.sort((x, y) => (y.dias || 0) - (x.dias || 0));
                const pendMais30 = pendentesAud.filter(p => (p.dias || 0) > 30).length;
                const pendMais60 = pendentesAud.filter(p => (p.dias || 0) > 60).length;

                // Onde cada operador mais erra: operador x tipo de alerta
                const catTotais = {};
                const opCat = {};
                audConf.forEach(a => {
                  const op = respDe(a);
                  a.itens.forEach(it => {
                    const cat = (it.categoria || 'Sem categoria').trim();
                    catTotais[cat] = (catTotais[cat] || 0) + 1;
                    (opCat[op] = opCat[op] || {})[cat] = ((opCat[op] || {})[cat] || 0) + 1;
                  });
                });
                const catsTopo = Object.entries(catTotais).sort((x, y) => y[1] - x[1]).slice(0, 6).map(([c]) => c);
                const mapaOp = Object.entries(opCat).map(([op, cats]) => {
                  const total = Object.values(cats).reduce((x, y) => x + y, 0);
                  const [maiorCat, maiorN] = Object.entries(cats).sort((x, y) => y[1] - x[1])[0] || ['—', 0];
                  return { op, cats, total, maiorCat, maiorN };
                }).sort((x, y) => y.total - x.total);
                const maxCel = Math.max(1, ...mapaOp.flatMap(m => catsTopo.map(c => m.cats[c] || 0)));

                // dados para gráficos
                const dadosSeveridade = [
                  { name: 'Graves',  value: totalGravesA,  color: '#dc2626' },
                  { name: 'Médios',  value: totalMediosA,  color: '#f59e0b' },
                  { name: 'Baixos',  value: totalBaixosA,  color: '#0ea5e9' },
                  { name: 'Manuais', value: totalManuaisA, color: '#94a3b8' },
                ].filter(d => d.value > 0);

                const dadosStatus = [
                  { name: 'Resolvidas', value: resolvidasCount, color: '#16a34a' },
                  { name: 'Pendentes',  value: pendentesCount,  color: '#cbd5e1' },
                ].filter(d => d.value > 0);

                return (
                  <>
                    {/* ── Período (data da auditoria) ── */}
                    <div className="card shadow-sm mb-4">
                      <div className="card-body py-3 d-flex flex-wrap align-items-center gap-2">
                        <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-calendar-alt me-2 text-primary"></i>Data da auditoria
                        </span>
                        <span className="text-muted small ms-2">De</span>
                        <input type="date" className="form-control form-control-sm" style={{ width: 160 }}
                          value={audDe} max={audAte || undefined} onChange={e => setAudDe(e.target.value)} />
                        <span className="text-muted small">até</span>
                        <input type="date" className="form-control form-control-sm" style={{ width: 160 }}
                          value={audAte} min={audDe || undefined} onChange={e => setAudAte(e.target.value)} />
                        {[['30', 'Últimos 30 dias'], ['90', 'Últimos 3 meses'], ['365', 'Último ano']].map(([d, l]) => (
                          <button key={d} type="button" className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              const ate = new Date(); const de = new Date(Date.now() - Number(d) * 86400000);
                              const iso = (x) => x.toISOString().slice(0, 10);
                              setAudDe(iso(de)); setAudAte(iso(ate));
                            }}>{l}</button>
                        ))}
                        {(audDe || audAte) && (
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setAudDe(''); setAudAte(''); }}>
                            <i className="fas fa-times me-1"></i>Limpar
                          </button>
                        )}
                        <span className="ms-auto text-muted small">
                          {totalAuditorias} auditoria(s) {audDe || audAte ? 'no período' : 'no total'} · só apontamentos confirmados
                        </span>
                      </div>
                    </div>

                    {/* ── KPIs principais ── */}
                    <div className="row g-4 mb-4">
                      {[
                        { icon: 'fa-search', color: '#1a3a6b', bg: '#e8edf5', value: totalAuditorias,  label: 'Auditorias Realizadas', sub: 'total registrado' },
                        { icon: 'fa-building', color: '#0d6efd', bg: '#e7f0ff', value: empresasUnicas, label: 'Empresas Analisadas',   sub: 'clientes únicos' },
                        { icon: 'fa-exclamation-triangle', color: '#dc3545', bg: '#fce8e8', value: totalAlertas, label: 'Total de Alertas', sub: `média ${mediAlertasPorAuditoria} por auditoria` },
                        { icon: 'fa-check-circle', color: '#198754', bg: '#e6f4ea', value: `${taxaResolucao}%`, label: 'Taxa de Resolução', sub: `${resolvidasCount} de ${totalAuditorias} resolvidas` },
                      ].map((k, i) => (
                        <div key={i} className="col-xl-3 col-md-6">
                          <div className="kpi-card" style={{ borderLeft: `4px solid ${k.color}`, background: k.bg }}>
                            <div className="kpi-icon" style={{ background: `${k.color}22`, color: k.color }}>
                              <i className={`fas ${k.icon}`}></i>
                            </div>
                            <div className="kpi-content">
                              <h3 className="kpi-value" style={{ color: k.color }}>{k.value}</h3>
                              <p className="kpi-label">{k.label}</p>
                              <small className="text-muted">{k.sub}</small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* ── Cobertura da auditoria por tributação ── */}
                    <div className="card shadow-sm mb-4">
                      <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 className="card-title mb-0">
                          <i className="fas fa-clipboard-check me-2 text-primary"></i>
                          Cobertura da auditoria por tributação
                        </h5>
                        <small className="text-muted">
                          Carteira ativa: <b>{totalCarteiraAud}</b> clientes · já auditadas: <b>{empresasAuditadasCarteira}</b>
                          {totalCarteiraAud > 0 && <> ({Math.round((empresasAuditadasCarteira / totalCarteiraAud) * 100)}%)</>}
                        </small>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          {Object.entries(REGIMES_AUD).map(([g, R]) => {
                            const lista = cobertura[g] || [];
                            const auditadas = lista.filter(x => x.situacao !== 'nunca').length;
                            const emDia = lista.filter(x => x.situacao === 'em_dia').length;
                            const vencidas = lista.filter(x => x.situacao === 'vencida').length;
                            const pct = lista.length ? Math.round((auditadas / lista.length) * 100) : 0;
                            return (
                              <div key={g} className="col-md-4">
                                <div role="button" tabIndex={0} className="card border-0 h-100"
                                  style={{ background: R.bg, cursor: 'pointer', borderLeft: `4px solid ${R.cor}` }}
                                  onClick={() => setModalCobertura(g)}
                                  onKeyDown={e => { if (e.key === 'Enter') setModalCobertura(g); }}
                                  title={`Ver as ${lista.length} empresas de ${R.nome}`}>
                                  <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div>
                                        <div className="fw-bold" style={{ color: R.cor }}>
                                          <i className={`fas ${R.icon} me-2`}></i>{R.nome}
                                        </div>
                                        <small className="text-muted">auditoria <b>{R.freq.toLowerCase()}</b></small>
                                      </div>
                                      <i className="fas fa-external-link-alt text-muted"></i>
                                    </div>
                                    <div className="d-flex align-items-end gap-2 mt-3">
                                      <span style={{ fontSize: '1.9rem', fontWeight: 700, lineHeight: 1, color: R.cor }}>{auditadas}</span>
                                      <span className="text-muted mb-1">de {lista.length} auditadas</span>
                                    </div>
                                    <div className="progress mt-2" style={{ height: 8 }}>
                                      <div className="progress-bar" style={{ width: `${pct}%`, background: R.cor }}></div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                      <span className="badge bg-success">{emDia} em dia</span>
                                      <span className="badge bg-danger">{vencidas} vencida(s)</span>
                                      <span className="badge bg-secondary">{lista.length - auditadas} nunca auditada(s)</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <small className="text-muted d-block mt-2">
                          "Em dia" = auditada dentro da frequência do regime (Simples até 12 meses, Presumido até 3 meses,
                          Lucro Real até 1 mês desde a última auditoria). Clique no card para ver as empresas.
                        </small>
                      </div>
                    </div>

                    {/* ── Breakdown severidade ── */}
                    <div className="row g-3 mb-4">
                      {[
                        { label: 'Alertas Graves',  value: totalGravesA,  color: '#dc3545', bg: '#fff1f2', icon: 'fa-exclamation-circle' },
                        { label: 'Alertas Médios',  value: totalMediosA,  color: '#d97706', bg: '#fffbeb', icon: 'fa-exclamation-triangle' },
                        { label: 'Alertas Baixos',  value: totalBaixosA,  color: '#0284c7', bg: '#f0f9ff', icon: 'fa-info-circle' },
                        { label: 'Observações Manuais', value: totalManuaisA, color: '#6c757d', bg: '#f8f9fa', icon: 'fa-pencil-alt' },
                        { label: 'Média Alertas/Auditoria', value: mediAlertasPorAuditoria, color: '#0891b2', bg: '#f0fdff', icon: 'fa-chart-bar' },
                      ].map((k, i) => (
                        <div key={i} className="col-6 col-md">
                          <div className="card border-0 shadow-sm h-100" style={{ background: k.bg }}>
                            <div className="card-body py-3 px-3 text-center">
                              <i className={`fas ${k.icon} mb-2`} style={{ color: k.color, fontSize: '1.3rem' }}></i>
                              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: k.color, lineHeight: 1 }}>{k.value}</div>
                              <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{k.label}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* ── Gráficos: distribuição + status ── */}
                    <div className="row g-4 mb-4">
                      <div className="col-lg-6">
                        <div className="card shadow-sm h-100">
                          <div className="card-header bg-white">
                            <h5 className="card-title mb-0">
                              <i className="fas fa-chart-pie me-2 text-danger"></i>
                              Distribuição por Severidade
                            </h5>
                          </div>
                          <div className="card-body">
                            {dadosSeveridade.length > 0
                              ? <DonutAud dados={dadosSeveridade} rotuloCentro="apontamentos" />
                              : <div className="text-center text-muted py-5"><i className="fas fa-inbox fa-3x mb-3"></i><p>Sem alertas no período</p></div>}
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="card shadow-sm h-100">
                          <div className="card-header bg-white">
                            <h5 className="card-title mb-0">
                              <i className="fas fa-tasks me-2 text-success"></i>
                              Status das Auditorias
                            </h5>
                          </div>
                          <div className="card-body">
                            {dadosStatus.length > 0
                              ? <DonutAud dados={dadosStatus} rotuloCentro="auditorias" />
                              : <div className="text-center text-muted py-5"><i className="fas fa-inbox fa-3x mb-3"></i><p>Sem dados no período</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Não resolvidos: há quanto tempo ── */}
                    <div className="card shadow-sm mb-4">
                      <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 className="card-title mb-0">
                          <i className="fas fa-hourglass-half me-2 text-danger"></i>
                          Não resolvidos — há quanto tempo
                        </h5>
                        <div className="d-flex flex-wrap gap-1">
                          <span className="badge bg-dark">{pendentesAud.length} em aberto</span>
                          <span className="badge bg-warning text-dark">{pendMais30} há mais de 30 dias</span>
                          <span className="badge bg-danger">{pendMais60} há mais de 60 dias</span>
                        </div>
                      </div>
                      <div className="card-body p-0">
                        {pendentesAud.length === 0 ? (
                          <div className="text-center text-muted py-4"><i className="fas fa-check-circle text-success me-2"></i>Nenhum apontamento confirmado em aberto no período.</div>
                        ) : (
                          <>
                            <div className="table-responsive" style={{ maxHeight: verTodosPend ? 'none' : 420 }}>
                              <table className="table table-sm table-hover align-middle mb-0">
                                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                  <tr>
                                    <th className="px-3 text-center">Dias em aberto</th>
                                    <th>Empresa</th>
                                    <th>Operador</th>
                                    <th className="text-center">Severidade</th>
                                    <th>Tipo</th>
                                    <th style={{ minWidth: 260 }}>Descrição</th>
                                    <th className="text-center">Auditada em</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(verTodosPend ? pendentesAud : pendentesAud.slice(0, 30)).map(p => (
                                    <tr key={p.id}>
                                      <td className="px-3 text-center">
                                        <span className={`badge ${p.dias > 60 ? 'bg-danger' : p.dias > 30 ? 'bg-warning text-dark' : 'bg-light text-dark'}`}
                                          style={{ fontSize: '0.85rem', minWidth: 56 }}>{p.dias != null ? `${p.dias} d` : '—'}</span>
                                        {p.andamento && <div className="text-muted" style={{ fontSize: '0.68rem' }}>em andamento</div>}
                                      </td>
                                      <td style={{ fontSize: '0.84rem' }}>
                                        <div className="fw-semibold">{p.cliente || '—'}</div>
                                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>{p.numero_dominio} · {p.periodo}</div>
                                      </td>
                                      <td style={{ fontSize: '0.84rem' }}>{p.operador}</td>
                                      <td className="text-center">
                                        <span className={`badge ${p.severidade === 'GRAVE' ? 'bg-danger' : p.severidade === 'MEDIO' ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
                                          {p.severidade === 'GRAVE' ? 'Grave' : p.severidade === 'MEDIO' ? 'Médio' : p.severidade === 'BAIXO' ? 'Baixo' : p.severidade || '—'}
                                        </span>
                                      </td>
                                      <td style={{ fontSize: '0.82rem' }}>{p.categoria}</td>
                                      <td className="text-muted" style={{ fontSize: '0.8rem' }}>{p.descricao}</td>
                                      <td className="text-center text-nowrap" style={{ fontSize: '0.82rem' }}>{p.data}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {pendentesAud.length > 30 && (
                              <div className="text-center py-2 border-top">
                                <button type="button" className="btn btn-sm btn-link" onClick={() => setVerTodosPend(v => !v)}>
                                  {verTodosPend ? 'Mostrar só os 30 mais antigos' : `Ver todos os ${pendentesAud.length}`}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* ── Onde cada operador mais aponta na auditoria ── */}
                    {mapaOp.length > 0 && (
                      <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <h5 className="card-title mb-0">
                            <i className="fas fa-th me-2 text-primary"></i>
                            Onde cada operador mais aponta na auditoria
                          </h5>
                          <small className="text-muted">apontamentos confirmados por tipo de alerta · quanto mais escuro, mais ocorrências</small>
                        </div>
                        <div className="card-body p-0">
                          <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: 4 }}>
                              <thead>
                                <tr>
                                  <th className="px-3 border-0">Operador</th>
                                  {catsTopo.map(c => <th key={c} className="text-center border-0" style={{ fontSize: '0.72rem', maxWidth: 120, whiteSpace: 'normal' }}>{c}</th>)}
                                  <th className="border-0" style={{ fontSize: '0.72rem' }}>Mais erra em</th>
                                </tr>
                              </thead>
                              <tbody>
                                {mapaOp.map(m => (
                                  <tr key={m.op}>
                                    <td className="px-3 fw-semibold border-0" style={{ fontSize: '0.86rem' }}>
                                      {m.op} <span className="text-muted fw-normal" style={{ fontSize: '0.75rem' }}>({m.total})</span>
                                    </td>
                                    {catsTopo.map(c => {
                                      const n = m.cats[c] || 0;
                                      const a = n / maxCel;
                                      return (
                                        <td key={c} className="text-center border-0"
                                          style={{ background: n ? `rgba(200, 30, 30, ${0.08 + a * 0.72})` : '#f5f7fa',
                                                   color: a > 0.5 ? '#fff' : '#0f172a', borderRadius: 8, fontWeight: n ? 700 : 400,
                                                   fontVariantNumeric: 'tabular-nums', minWidth: 64 }}>
                                          {n || <span className="text-muted">·</span>}
                                        </td>
                                      );
                                    })}
                                    <td className="border-0" style={{ fontSize: '0.82rem' }}>
                                      <b>{m.maiorCat}</b> <span className="text-muted">({m.maiorN})</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Evolução mensal ── */}
                    {evolAuditoria.length > 0 && (
                      <div className="row g-4 mb-4">
                        <div className="col-12">
                          <div className="card shadow-sm">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <h5 className="card-title mb-0">
                                <i className="fas fa-chart-area me-2 text-primary"></i>
                                Auditorias feitas x Retorno (resolvidas) — {evolAgrupar === 'semana' ? 'por semana' : 'por mês'}
                              </h5>
                              <div className="d-flex flex-wrap gap-2 align-items-center">
                                <div className="btn-group btn-group-sm" role="group" aria-label="Agrupar">
                                  <button type="button" className={`btn btn-sm ${evolAgrupar === 'semana' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setEvolAgrupar('semana')}>Semana</button>
                                  <button type="button" className={`btn btn-sm ${evolAgrupar === 'mes' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setEvolAgrupar('mes')}>Mês</button>
                                </div>
                                <select className="form-select form-select-sm" style={{ width: 'auto' }} value={evolPeriodo}
                                  onChange={e => setEvolPeriodo(e.target.value)} aria-label="Período do gráfico">
                                  <option value="30">Últimos 30 dias</option>
                                  <option value="90">Últimos 3 meses</option>
                                  <option value="180">Últimos 6 meses</option>
                                  <option value="365">Último ano</option>
                                  <option value="todos">Todo o histórico</option>
                                </select>
                              </div>
                            </div>
                            <div className="card-body">
                              {evolFiltrada.length === 0 ? (
                                <div className="text-center text-muted py-5">Nenhuma auditoria no período escolhido.</div>
                              ) : (
                              <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={evolFiltrada} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                  <defs>
                                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor="#00205b" stopOpacity={0.28} />
                                      <stop offset="100%" stopColor="#00205b" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorResolv" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor="#16a34a" stopOpacity={0.28} />
                                      <stop offset="100%" stopColor="#16a34a" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorGravesA" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor="#dc2626" stopOpacity={0.22} />
                                      <stop offset="100%" stopColor="#dc2626" stopOpacity={0} />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid vertical={false} stroke="#eef1f5" />
                                  <XAxis dataKey="mes" {...EIXO_AUD} dy={8} />
                                  <YAxis {...EIXO_AUD} allowDecimals={false} width={32} />
                                  <Tooltip content={<TooltipAud />} cursor={{ stroke: '#cbd5e1', strokeDasharray: '4 4' }} />
                                  <Legend verticalAlign="top" align="right" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingBottom: 12 }} />
                                  <Area type="monotone" dataKey="total"     name="Auditorias feitas"    stroke="#00205b" strokeWidth={2.5} fill="url(#colorTotal)"   dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
                                  <Area type="monotone" dataKey="resolvidas" name="Retorno (resolvidas)" stroke="#16a34a" strokeWidth={2.5} fill="url(#colorResolv)"  dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
                                  <Area type="monotone" dataKey="graves"    name="Alertas graves"       stroke="#dc2626" strokeWidth={2}   fill="url(#colorGravesA)" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
                                </AreaChart>
                              </ResponsiveContainer>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Ranking do time por regime + menos graves/médios ── */}
                    <div className="row g-4 mb-4">
                      <div className="col-lg-8">
                        <div className="card shadow-sm h-100">
                          <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="card-title mb-0">
                              <i className="fas fa-users me-2 text-primary"></i>
                              Ranking do time por regime de tributação
                            </h5>
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={rankRegime}
                                onChange={e => setRankRegime(e.target.value)} aria-label="Regime">
                                <option value="">Todos os regimes</option>
                                <option value="SN">Simples Nacional</option>
                                <option value="LP">Lucro Presumido</option>
                                <option value="LR">Lucro Real (mensal/trimestral)</option>
                                <option value="OUTROS">Outros</option>
                              </select>
                              <div className="btn-group btn-group-sm" role="group" aria-label="Ordem">
                                <button type="button" className={`btn btn-sm ${rankOrdem === 'mais' ? 'btn-danger' : 'btn-outline-danger'}`}
                                  onClick={() => setRankOrdem('mais')}>Mais erros</button>
                                <button type="button" className={`btn btn-sm ${rankOrdem === 'menos' ? 'btn-success' : 'btn-outline-success'}`}
                                  onClick={() => setRankOrdem('menos')}>Menos erros</button>
                              </div>
                            </div>
                          </div>
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <table className="table table-sm table-hover align-middle mb-0">
                                <thead style={{ background: '#e8edf5', fontSize: '0.78rem', fontWeight: 700, color: '#2c3e6b', textTransform: 'uppercase' }}>
                                  <tr>
                                    <th className="px-3">#</th>
                                    <th>Colaborador</th>
                                    <th className="text-center">Auditorias</th>
                                    <th className="text-center">Apontamentos</th>
                                    <th className="text-center">Resolvidos</th>
                                    <th className="text-center">Graves</th>
                                    <th className="text-center">Médios</th>
                                    <th className="text-center" title="Média de dias entre a auditoria e a resolução do item">Tempo de resolução</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rankingTime.map((r, i) => (
                                    <tr key={r.operador}>
                                      <td className="px-3 text-muted">{i + 1}º</td>
                                      <td className="fw-semibold" style={{ fontSize: '0.86rem' }}>{r.operador}</td>
                                      <td className="text-center">{r.audConf || <span className="text-muted">0</span>}</td>
                                      <td className="text-center"><span className="badge bg-primary">{r.total}</span></td>
                                      <td className="text-center">
                                        <span className="badge bg-success">{r.resolvidos}</span>
                                        {r.total > 0 && <small className="text-muted ms-1">{r.pctResolvido}%</small>}
                                      </td>
                                      <td className="text-center"><span className={`badge ${r.graves ? 'bg-danger' : 'bg-light text-dark'}`}>{r.graves}</span></td>
                                      <td className="text-center"><span className={`badge ${r.medios ? 'bg-warning text-dark' : 'bg-light text-dark'}`}>{r.medios}</span></td>
                                      <td className="text-center" style={{ fontSize: '0.85rem' }}>
                                        {r.tempoMedio == null ? <span className="text-muted">—</span>
                                          : <span className={r.tempoMedio > 30 ? 'text-danger fw-semibold' : ''}>{r.tempoMedio} dia(s)</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <small className="text-muted d-block px-3 py-2">
                              Colaborador = responsável pela empresa auditada. Todo o time aparece, inclusive quem não teve apontamento.
                              {' Contando só apontamentos confirmados pelo operador, como no boletim.'}
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-4">
                        <div className="card shadow-sm h-100">
                          <div className="card-header bg-white">
                            <h5 className="card-title mb-0">
                              <i className="fas fa-medal me-2 text-success"></i>
                              Menos alertas graves e médios
                            </h5>
                          </div>
                          <div className="card-body">
                            {menosGraves.length === 0 ? (
                              <div className="text-center text-muted py-4">Sem auditorias no recorte.</div>
                            ) : menosGraves.map((r, i) => (
                              <div key={r.operador} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                                <div className="d-flex align-items-center gap-2">
                                  <span className={`badge ${i === 0 ? 'bg-success' : 'bg-light text-dark'}`}>{i + 1}º</span>
                                  <span className="fw-semibold" style={{ fontSize: '0.88rem' }}>{r.operador}</span>
                                </div>
                                <div className="text-end">
                                  <span className="badge bg-danger me-1">{r.graves} graves</span>
                                  <span className="badge bg-warning text-dark">{r.medios} médios</span>
                                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                    {r.gmPorAud.toFixed(1)} por auditoria · {r.audConf} auditoria(s)
                                  </div>
                                </div>
                              </div>
                            ))}
                            <small className="text-muted d-block mt-2">Ordenado pela média de graves + médios por auditoria, para não favorecer quem teve poucas auditorias.</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Ranking dos erros mais frequentes ── */}
                    {rankingErros.length > 0 && (
                      <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                          <h5 className="card-title mb-0">
                            <i className="fas fa-list-ol me-2 text-danger"></i>
                            Erros que mais aparecem nas auditorias
                          </h5>
                          <small className="text-muted">{rankRegime ? REGIMES_AUD[rankRegime]?.nome || 'Outros' : 'Todos os regimes'}{' · só confirmados'}</small>
                        </div>
                        <div className="card-body p-0">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover align-middle mb-0">
                              <thead style={{ background: '#e8edf5', fontSize: '0.78rem', fontWeight: 700, color: '#2c3e6b', textTransform: 'uppercase' }}>
                                <tr>
                                  <th className="px-3">#</th>
                                  <th>Tipo de alerta</th>
                                  <th style={{ minWidth: 320 }}>Descrição mais frequente</th>
                                  <th className="text-center">Ocorrências</th>
                                  <th className="text-center">Graves</th>
                                  <th className="text-center">Empresas</th>
                                  <th className="text-center">Resolvidos</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rankingErros.map((e, i) => (
                                  <tr key={e.categoria}>
                                    <td className="px-3 text-muted">{i + 1}º</td>
                                    <td className="fw-semibold" style={{ fontSize: '0.86rem' }}>{e.categoria}</td>
                                    <td style={{ fontSize: '0.8rem' }}>
                                      {e.topDesc.map(([d, n]) => (
                                        <div key={d} className="text-muted">{d} <span className="badge bg-light text-dark ms-1">{n}x</span></div>
                                      ))}
                                    </td>
                                    <td className="text-center"><span className="badge bg-dark">{e.total}</span></td>
                                    <td className="text-center"><span className={`badge ${e.graves ? 'bg-danger' : 'bg-light text-dark'}`}>{e.graves}</span></td>
                                    <td className="text-center">{e.nEmpresas}</td>
                                    <td className="text-center"><small>{e.total ? Math.round((e.resolvidos / e.total) * 100) : 0}%</small></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Top empresas com mais alertas ── */}
                    {topEmpresas.length > 0 && (
                      <div className="row mb-4">
                        <div className="col-12">
                          <div className="card shadow-sm">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                              <h5 className="card-title mb-0">
                                <i className="fas fa-fire me-2 text-danger"></i>
                                Top Empresas — Mais Alertas Graves
                              </h5>
                              <span className="badge bg-danger fs-6">{topEmpresas.length} empresa{topEmpresas.length > 1 ? 's' : ''}</span>
                            </div>
                            <div className="card-body p-0">
                              <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                  <thead style={{ background: '#e8edf5', fontSize: '0.82rem', fontWeight: 700, color: '#2c3e6b', textTransform: 'uppercase' }}>
                                    <tr>
                                      <th className="px-3">#</th>
                                      <th>Empresa</th>
                                      <th>Nº Domínio</th>
                                      <th>Período</th>
                                      <th>Operador</th>
                                      <th className="text-center">Graves</th>
                                      <th className="text-center">Médios</th>
                                      <th className="text-center">Baixos</th>
                                      <th className="text-center">Total</th>
                                      <th className="text-center">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {topEmpresas.map((e, i) => (
                                      <tr key={i} className={e.graves > 0 && !e.resolvido ? 'table-danger' : ''}>
                                        <td className="px-3">
                                          <span className={`badge ${i === 0 ? 'bg-danger' : i === 1 ? 'bg-warning text-dark' : i === 2 ? 'bg-secondary' : 'bg-light text-dark'}`}>
                                            {i + 1}º
                                          </span>
                                        </td>
                                        <td className="fw-semibold" style={{ fontSize: '0.88rem' }}>
                                          <i className="fas fa-building me-2 text-muted"></i>{e.cliente}
                                        </td>
                                        <td><code style={{ fontSize: '0.80rem' }}>{e.numero_dominio || '—'}</code></td>
                                        <td style={{ fontSize: '0.83rem' }}>{e.periodo || '—'}</td>
                                        <td><span className="badge bg-info">{e.operador || '—'}</span></td>
                                        <td className="text-center">
                                          <span className={`badge ${e.graves > 0 ? 'bg-danger' : 'bg-success'} fs-6`}>{e.graves}</span>
                                        </td>
                                        <td className="text-center">
                                          <span className={`badge ${e.medios > 0 ? 'bg-warning text-dark' : 'bg-light text-dark'} fs-6`}>{e.medios}</span>
                                        </td>
                                        <td className="text-center">
                                          <span className="badge bg-info text-dark fs-6">{e.baixos}</span>
                                        </td>
                                        <td className="text-center">
                                          <span className="badge bg-dark fs-6">{e.totalAlertas}</span>
                                        </td>
                                        <td className="text-center">
                                          <span className={`badge ${e.resolvido ? 'bg-success' : 'bg-secondary'}`}>
                                            {e.resolvido ? 'Resolvido' : 'Pendente'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Modal: empresas por tributação ── */}
                    {modalCobertura && REGIMES_AUD[modalCobertura] && (
                      <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                        onClick={() => setModalCobertura(null)}>
                        <div className="modal-dialog modal-xl modal-dialog-scrollable" style={{ maxWidth: 'min(1600px, 96vw)' }} onClick={e => e.stopPropagation()}>
                          <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                              <div>
                                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                                  <i className={`fas ${REGIMES_AUD[modalCobertura].icon} me-2`} style={{ color: REGIMES_AUD[modalCobertura].cor }}></i>
                                  {REGIMES_AUD[modalCobertura].nome} — auditoria {REGIMES_AUD[modalCobertura].freq.toLowerCase()}
                                </h5>
                                <small style={{ color: '#6c757d' }}>{(cobertura[modalCobertura] || []).length} empresa(s) na carteira ativa · vencidas primeiro</small>
                              </div>
                              <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setModalCobertura(null)}></button>
                            </div>
                            <div className="modal-body p-0">
                              <div className="table-responsive">
                                <table className="table table-sm table-hover align-middle mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th className="px-3">Empresa</th>
                                      <th>Nº Dom.</th>
                                      <th>Operador</th>
                                      <th className="text-center">Auditorias</th>
                                      <th>Competências auditadas</th>
                                      <th className="text-center">Última auditoria</th>
                                      <th className="text-center">Situação</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(cobertura[modalCobertura] || []).map(x => (
                                      <tr key={x.numero_dominio}>
                                        <td className="px-3 fw-semibold" style={{ fontSize: '0.86rem' }}>{x.empresa}</td>
                                        <td><code style={{ fontSize: '0.8rem' }}>{x.numero_dominio}</code></td>
                                        <td style={{ fontSize: '0.84rem' }}>{x.operador || '—'}</td>
                                        <td className="text-center">{x.audConf}</td>
                                        <td className="text-muted" style={{ fontSize: '0.78rem' }}>{x.competencias || '—'}</td>
                                        <td className="text-center" style={{ fontSize: '0.84rem' }}>
                                          {x.ultima}{x.dias != null && <div className="text-muted" style={{ fontSize: '0.72rem' }}>há {x.dias} dia(s)</div>}
                                        </td>
                                        <td className="text-center">
                                          {x.situacao === 'em_dia' ? <span className="badge bg-success">Em dia</span>
                                            : x.situacao === 'vencida' ? <span className="badge bg-danger">Vencida</span>
                                              : <span className="badge bg-secondary">Nunca auditada</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Empty state */}
                    {totalAuditorias === 0 && (
                      <div className="card border-0 shadow-sm">
                        <div className="card-body text-center py-5">
                          <i className="fas fa-search fa-4x text-muted mb-3" style={{ opacity: 0.3 }}></i>
                          <h5 className="text-muted">Nenhuma auditoria registrada</h5>
                          <p className="text-muted mb-0">As auditorias salvas na página de resultado aparecerão aqui.</p>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Modal de Observação */}
        {modalObservacao.show && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-comment-alt me-2"></i>
                    Observação da Pendência
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setModalObservacao({ show: false, texto: '', empresa: '' })}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-light border mb-3">
                    <small className="text-muted">
                      <i className="fas fa-building me-2"></i>
                      <strong>Empresa:</strong>
                    </small>
                    <p className="mb-0 mt-1 fw-semibold">{modalObservacao.empresa}</p>
                  </div>
                  <div className="p-3 bg-light rounded">
                    <small className="text-muted d-block mb-2">
                      <i className="fas fa-comment me-2"></i>
                      <strong>Observação:</strong>
                    </small>
                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {modalObservacao.texto}
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setModalObservacao({ show: false, texto: '', empresa: '' })}
                  >
                    <i className="fas fa-times me-2"></i>
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Entregas (card Total de Entregas) */}
        {modalEntregas.show && (
          <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div
              className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
              style={{ maxWidth: '95vw', width: '1400px' }}
            >
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-file-invoice me-2"></i>
                    Total de Entregas — {entregasFiltradas.length} registro(s)
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setModalEntregas({ show: false })}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  {entregasFiltradas.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th><i className="fas fa-calendar me-2"></i>Data</th>
                            <th><i className="fas fa-building me-2"></i>Empresa</th>
                            <th><i className="fas fa-user me-2"></i>Operador</th>
                            <th><i className="fas fa-tag me-2"></i>Tipo</th>
                            <th><i className="fas fa-info-circle me-2"></i>Status</th>
                            <th><i className="fas fa-comment me-2"></i>Obs. Atraso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entregasFiltradas.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <small className="text-muted">
                                  <i className="fas fa-calendar me-1"></i>
                                  {item.data_hoje?.split(' ')[0] || '-'}
                                </small>
                              </td>
                              <td className="fw-semibold">{item.empresa || '-'}</td>
                              <td><span className="badge bg-info">{item.operador || '-'}</span></td>
                              <td>
                                <span className={`badge ${
                                  item.tipo_entrega === 'Completa' ? 'bg-success' :
                                  item.tipo_entrega === 'Parcial' ? 'bg-warning text-dark' :
                                  item.tipo_entrega === 'Atrasada' ? 'bg-danger' :
                                  item.tipo_entrega === 'Atrasada e Parcial' ? 'bg-danger' : 'bg-secondary'
                                }`}>
                                  {item.tipo_entrega || '-'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${
                                  item.status_entrega?.toLowerCase().includes('atrasado') ? 'bg-danger' : 'bg-success'
                                }`}>
                                  {item.status_entrega || '-'}
                                </span>
                              </td>
                              <td style={{ maxWidth: '280px' }}>
                                {item.texto_livre && item.texto_livre.trim() !== '' ? (
                                  <small
                                    className="text-danger fst-italic"
                                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                    title={item.texto_livre}
                                  >
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    {item.texto_livre.length > 60 ? item.texto_livre.substring(0, 60) + '…' : item.texto_livre}
                                  </small>
                                ) : (
                                  <small className="text-muted">
                                    <i className="fas fa-minus me-1"></i>
                                    Sem observação
                                  </small>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-inbox fa-3x mb-3"></i>
                      <p>Nenhuma entrega no período selecionado</p>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <small className="text-muted me-auto">
                    <i className="fas fa-filter me-1"></i>
                    Respeita os filtros aplicados (período, operador e tipo)
                  </small>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setModalEntregas({ show: false })}
                  >
                    <i className="fas fa-times me-2"></i>
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gestao_contabil;